﻿﻿﻿import { Component, MeshComponent, Property, Mesh, MeshIndexType, MeshAttribute } from '@wonderlandengine/api';
import { vec3, quat } from 'gl-matrix';

// -------------------------------------------------------------------------------
//  SECTION 1 � SIMPLEX NOISE  (seeded, zero-dependency, self-contained)
// -------------------------------------------------------------------------------

const _F2 = 0.5 * (Math.sqrt(3.0) - 1.0);
const _G2 = (3.0 - Math.sqrt(3.0)) / 6.0;
const _grad2x = new Float32Array([1, -1, 1, -1, 1, -1, 1, -1, 0, 0, 0, 0]);
const _grad2y = new Float32Array([1, 1, -1, -1, 0, 0, 0, 0, 1, -1, 1, -1]);
function _dot2(gi, x, y) { return _grad2x[gi] * x + _grad2y[gi] * y; }

class SimplexNoise2D {
    constructor(seed = 12345) {
        this.perm = new Uint8Array(512);
        let s = ((seed ^ 0xDEADBEEF) >>> 0) || 1;
        const rnd = () => { s = (Math.imul(1664525, s) + 1013904223) >>> 0; return s / 0x100000000; };
        const p = new Uint8Array(256);
        for (let i = 0; i < 256; i++) p[i] = i;
        for (let i = 255; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); const tmp = p[i]; p[i] = p[j]; p[j] = tmp; }
        for (let i = 0; i < 512; i++) this.perm[i] = p[i & 255];
    }

    noise(xin, yin) {
        const s = (xin + yin) * _F2;
        const i = Math.floor(xin + s), j = Math.floor(yin + s);
        const t = (i + j) * _G2;
        const x0 = xin - (i - t), y0 = yin - (j - t);
        const i1 = x0 > y0 ? 1 : 0, j1 = x0 > y0 ? 0 : 1;
        const x1 = x0 - i1 + _G2, y1 = y0 - j1 + _G2;
        const x2 = x0 - 1 + 2 * _G2, y2 = y0 - 1 + 2 * _G2;
        const ii = i & 255, jj = j & 255;
        const gi0 = this.perm[ii + this.perm[jj]] % 12;
        const gi1 = this.perm[ii + i1 + this.perm[jj + j1]] % 12;
        const gi2 = this.perm[ii + 1 + this.perm[jj + 1]] % 12;
        let n0 = 0, n1 = 0, n2 = 0;
        let t0 = 0.5 - x0 * x0 - y0 * y0; if (t0 >= 0) { t0 *= t0; n0 = t0 * t0 * _dot2(gi0, x0, y0); }
        let t1 = 0.5 - x1 * x1 - y1 * y1; if (t1 >= 0) { t1 *= t1; n1 = t1 * t1 * _dot2(gi1, x1, y1); }
        let t2 = 0.5 - x2 * x2 - y2 * y2; if (t2 >= 0) { t2 *= t2; n2 = t2 * t2 * _dot2(gi2, x2, y2); }
        return 70.0 * (n0 + n1 + n2);
    }

    fbm(x, y, oct = 6, lac = 2.0, gain = 0.5) {
        let v = 0, a = 0.5, f = 1, mx = 0;
        for (let o = 0; o < oct; o++) { v += this.noise(x * f, y * f) * a; mx += a; a *= gain; f *= lac; }
        return v / mx; // ? [-1, 1]
    }
}

// -------------------------------------------------------------------------------
//  SECTION 2 � BIOME REGISTRY
//
//  Each biome is a plain object describing terrain look and feel. To add a new
//  biome: register it with BiomeManager.register(). No other code changes.
//
//  Biome fields:
//    id          � unique string key (debug / serialisation)
//    tempRange   � [min, max] normalised temperature ? [-1, 1]
//    moistRange  � [min, max] normalised moisture  ? [-1, 1]
//    heightAmp   � vertical scale multiplier (relative to terrainHeight prop)
//    heightShape � fn(continent, detail) ? normalised height in [-1, 1]
//    surfaceColor� fn(h, wx, wz, noise, out) ? writes [r,g,b,a] into `out`
//    subColors   � array of [r,g,b,a] for subsurface depth layers
//    columnDepth � how many layers below surface are solid
//    resource    � fn(h, isSurface, depthFromSurface) ? { type, amount, desc }
// -------------------------------------------------------------------------------

/** Smooth cubic interpolation */
function _smoothstep(a, b, t) { t = Math.max(0, Math.min(1, t)); t = t * t * (3 - 2 * t); return a + (b - a) * t; }

/** Lerp between two RGBA arrays, write into `out` Float32Array */
function _lerpColor(out, a, b, t) {
    out[0] = a[0] + (b[0] - a[0]) * t;
    out[1] = a[1] + (b[1] - a[1]) * t;
    out[2] = a[2] + (b[2] - a[2]) * t;
    out[3] = 1.0;
}

// -- Biome colour helpers ------------------------------------------------------

function _stopColor(stops, h, out) {
    const last = stops.length - 1;
    if (h <= stops[0].h) { out[0] = stops[0].c[0]; out[1] = stops[0].c[1]; out[2] = stops[0].c[2]; out[3] = 1; return; }
    if (h >= stops[last].h) { out[0] = stops[last].c[0]; out[1] = stops[last].c[1]; out[2] = stops[last].c[2]; out[3] = 1; return; }
    for (let i = 0; i < last; i++) {
        if (h >= stops[i].h && h <= stops[i + 1].h) {
            const t = (h - stops[i].h) / (stops[i + 1].h - stops[i].h);
            _lerpColor(out, stops[i].c, stops[i + 1].c, t);
            return;
        }
    }
    out[0] = stops[0].c[0]; out[1] = stops[0].c[1]; out[2] = stops[0].c[2]; out[3] = 1;
}

// -- Biome definitions ---------------------------------------------------------
//
//  All biomes use realistic height-based colour ramps.
//  Height h is normalised [-1, 1] where 0 = sea level.
//
//    h < -0.05  -> underwater  (deep seabed -> shallow water)
//    h = 0+-0.04 -> shoreline / beach / tidal flat
//    h > 0.04   -> land        (grass -> forest -> highland -> rock -> snow)
//
//  Each biome tunes the ramp to its climate character (arctic, tropical,
//  volcanic, arid, etc.) and adds noise-driven micro-variation in
//  surfaceColor() for patches, strata and weathering effects.
// -------------------------------------------------------------------------------

/**
 * BIOME: TEMPERATE GRASSLANDS
 * Rolling lowland plains with clear coastal waters, white-sand beaches,
 * bright grass valleys, mixed forest mid-slopes, and snowcapped peaks.
 */
const BIOME_GRASSLANDS = {
    id: 'grasslands',
    tempRange: [-0.2, 0.5],
    moistRange: [0.1, 1.0],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const base   = continent * 0.80 + detail * 0.20;
        const crater = Math.pow(Math.max(0, Math.abs(detail) - 0.55), 2) * -1.2;
        const mtn    = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return base + crater + mtn * 0.35;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.09, 0.24, 1] }, // deep seabed - dark navy
        { h: -0.55, c: [0.06, 0.18, 0.42, 1] }, // ocean floor - deep blue
        { h: -0.20, c: [0.10, 0.34, 0.62, 1] }, // open water - mid blue
        { h: -0.05, c: [0.22, 0.58, 0.72, 1] }, // coastal shallows - teal
        { h:  0.00, c: [0.80, 0.76, 0.54, 1] }, // sandy beach - warm sand
        { h:  0.05, c: [0.74, 0.70, 0.48, 1] }, // dry sand fade
        { h:  0.10, c: [0.48, 0.68, 0.24, 1] }, // fresh lowland grass
        { h:  0.28, c: [0.28, 0.52, 0.14, 1] }, // lush meadow
        { h:  0.45, c: [0.20, 0.40, 0.12, 1] }, // mixed forest
        { h:  0.60, c: [0.46, 0.40, 0.26, 1] }, // highland scrub / heather
        { h:  0.72, c: [0.52, 0.48, 0.42, 1] }, // rocky slope
        { h:  0.84, c: [0.62, 0.60, 0.58, 1] }, // bare mountain rock
        { h:  0.92, c: [0.90, 0.92, 0.96, 1] }, // snow line
        { h:  1.00, c: [1.00, 1.00, 1.00, 1] }, // peak snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Patchy grass variation: bright vs darker forest tones
        if (h > 0.05 && h < 0.65) {
            const patch = n.noise(wx * 0.012, wz * 0.012) * 0.5 + 0.5;
            const dry   = n.noise(wx * 0.035, wz * 0.035) * 0.5 + 0.5;
            out[0] += (patch - 0.5) * 0.06;
            out[1] += (patch - 0.5) * 0.08 - dry * 0.04;
            out[2] -= dry * 0.03;
        }
        // Snow sparkle on peak
        if (h >= 0.92) {
            const sparkle = n.noise(wx * 0.18, wz * 0.18) * 0.03;
            out[0] = Math.min(1, out[0] + sparkle);
            out[1] = Math.min(1, out[1] + sparkle);
            out[2] = Math.min(1, out[2] + sparkle);
        }
    },
    subColors: [
        [0.32, 0.28, 0.20, 1], // topsoil
        [0.40, 0.34, 0.24, 1], // subsoil
        [0.50, 0.44, 0.34, 1], // clay
        [0.48, 0.44, 0.40, 1], // bedrock
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return depth >= 3 ? { type: 'crystal', amount: 2, desc: 'Quartz (+2)' }
            : { type: 'alloy', amount: 2, desc: 'Iron Ore (+2)' };
        if (h <= -0.3) return { type: 'crystal', amount: 3, desc: 'Sea Minerals (+3)' };
        if (h <= 0.55) return { type: 'alloy', amount: 2, desc: 'Topsoil Minerals (+2)' };
        return { type: 'crystal', amount: 2, desc: 'Mountain Ore (+2)' };
    }
};

/**
 * BIOME: COASTAL CLIFFS
 * Dramatic limestone sea-cliffs and sheltered bays. Turquoise shallows,
 * gulls wheeling above grey-green scrub headlands, chalky cliff tops.
 */
const BIOME_FALLEN_CITY = {
    id: 'fallen_city',
    tempRange: [-0.5, 0.3],
    moistRange: [-1.0, 0.2],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const urban = _smoothstep(0, 1, (Math.pow(Math.abs(detail), 0.35) - 0.72) / 0.16);
        const mtn   = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return (continent * 0.65) + (urban * Math.abs(detail) * 1.4) + mtn * 0.30;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.10, 0.26, 1] }, // deep ocean
        { h: -0.50, c: [0.06, 0.20, 0.48, 1] }, // mid ocean
        { h: -0.18, c: [0.12, 0.44, 0.68, 1] }, // coastal blue
        { h: -0.04, c: [0.28, 0.68, 0.78, 1] }, // turquoise shallows
        { h:  0.00, c: [0.82, 0.78, 0.56, 1] }, // beach / sand
        { h:  0.06, c: [0.60, 0.58, 0.44, 1] }, // damp sand
        { h:  0.14, c: [0.42, 0.56, 0.20, 1] }, // coastal grass
        { h:  0.30, c: [0.28, 0.44, 0.14, 1] }, // shrub / scrub
        { h:  0.50, c: [0.50, 0.44, 0.28, 1] }, // cliff earth
        { h:  0.68, c: [0.56, 0.52, 0.46, 1] }, // limestone cliff face
        { h:  0.82, c: [0.66, 0.64, 0.62, 1] }, // grey cliff top
        { h:  0.93, c: [0.88, 0.90, 0.94, 1] }, // snow line
        { h:  1.00, c: [1.00, 1.00, 1.00, 1] }, // peak snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Wet rock darkening near shoreline
        if (h > -0.06 && h < 0.12) {
            const wet = n.noise(wx * 0.06, wz * 0.06) * 0.5 + 0.5;
            if (wet > 0.55) {
                const t = (wet - 0.55) * 2.2;
                out[0] *= (1 - t * 0.18);
                out[1] *= (1 - t * 0.12);
                out[2] *= (1 - t * 0.08);
            }
        }
        // Snow sparkle
        if (h >= 0.92) {
            const s = n.noise(wx * 0.16, wz * 0.16) * 0.025;
            out[0] = Math.min(1, out[0] + s);
            out[1] = Math.min(1, out[1] + s);
            out[2] = Math.min(1, out[2] + s);
        }
    },
    subColors: [
        [0.52, 0.48, 0.40, 1], // chalky limestone
        [0.44, 0.40, 0.34, 1],
        [0.36, 0.32, 0.28, 1],
        [0.28, 0.24, 0.22, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return { type: 'alloy', amount: 2, desc: 'Limestone (+2)' };
        if (h > 0.5) return { type: 'crystal', amount: 3, desc: 'Cliff Mineral (+3)' };
        return { type: 'alloy', amount: 2, desc: 'Coastal Stone (+2)' };
    }
};

/**
 * BIOME: ARID STEPPE
 * Semi-arid plateau - khaki-brown dry grass, cracked clay pans, sparse
 * thornbush. Distant highland escarpments pale blue-grey in the haze.
 */
const BIOME_CYBER_GRID = {
    id: 'cyber_grid',
    tempRange: [-1.0, -0.3],
    moistRange: [-1.0, 0.0],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const base = continent * 0.85 + detail * 0.15;
        const mtn  = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return base + Math.sin(base * Math.PI * 5) * 0.025 + mtn * 0.40;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.10, 0.24, 1] }, // deep water
        { h: -0.45, c: [0.08, 0.22, 0.44, 1] }, // open water
        { h: -0.10, c: [0.22, 0.48, 0.62, 1] }, // shallow brackish
        { h:  0.00, c: [0.76, 0.72, 0.54, 1] }, // dry sand flat
        { h:  0.08, c: [0.70, 0.64, 0.42, 1] }, // cracked clay
        { h:  0.22, c: [0.62, 0.56, 0.32, 1] }, // dry straw-yellow
        { h:  0.38, c: [0.52, 0.46, 0.26, 1] }, // khaki scrub
        { h:  0.55, c: [0.48, 0.42, 0.30, 1] }, // ochre highland
        { h:  0.70, c: [0.54, 0.50, 0.42, 1] }, // rocky escarpment
        { h:  0.82, c: [0.62, 0.58, 0.54, 1] }, // bare rock
        { h:  0.92, c: [0.88, 0.90, 0.92, 1] }, // light snow cap
        { h:  1.00, c: [0.98, 0.99, 1.00, 1] }, // peak snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Cracked clay texture in dry lowlands
        if (h > 0.02 && h < 0.35) {
            const crack = n.noise(wx * 0.08, wz * 0.08);
            if (crack > 0.52) {
                const t = Math.min(1, (crack - 0.52) * 4);
                out[0] = Math.min(1, out[0] * (1 + t * 0.12));
                out[1] *= (1 - t * 0.04);
                out[2] *= (1 - t * 0.08);
            }
        }
    },
    subColors: [
        [0.58, 0.50, 0.34, 1], // dry clay
        [0.50, 0.42, 0.28, 1],
        [0.42, 0.36, 0.24, 1],
        [0.34, 0.30, 0.22, 1],
    ],
    resource(h, isSurface, depth) {
        return { type: 'alloy', amount: 2, desc: 'Dry Minerals (+2)' };
    }
};

/**
 * BIOME: AUTUMNAL HIGHLANDS
 * Deciduous hillsides ablaze with autumn - rust-orange and amber canopy
 * above warm brown soil; rocky ridges dusted with early frost.
 */
const BIOME_FLESH_HIVE = {
    id: 'flesh_hive',
    tempRange: [0.4, 1.0],
    moistRange: [0.5, 1.0],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const ribs = Math.sin(continent * 8.0) * 0.12;
        const mtn  = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return (continent * 0.82) + (detail * 0.18) + ribs + mtn * 0.38;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.10, 0.26, 1] }, // deep lake / water
        { h: -0.50, c: [0.06, 0.20, 0.46, 1] }, // lake blue
        { h: -0.12, c: [0.14, 0.40, 0.62, 1] }, // shallow water
        { h:  0.00, c: [0.74, 0.68, 0.48, 1] }, // autumn sand/shore
        { h:  0.08, c: [0.48, 0.44, 0.22, 1] }, // autumn meadow
        { h:  0.22, c: [0.70, 0.38, 0.10, 1] }, // orange foliage
        { h:  0.38, c: [0.60, 0.28, 0.08, 1] }, // deep amber canopy
        { h:  0.52, c: [0.52, 0.30, 0.12, 1] }, // rust-brown forest floor
        { h:  0.66, c: [0.46, 0.38, 0.24, 1] }, // highland scrub
        { h:  0.78, c: [0.54, 0.50, 0.44, 1] }, // rocky ridge
        { h:  0.90, c: [0.78, 0.80, 0.84, 1] }, // frost-dusted rock
        { h:  1.00, c: [0.96, 0.97, 1.00, 1] }, // snow cap
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Autumn colour variation: red vs yellow foliage patches
        if (h > 0.08 && h < 0.60) {
            const variety = n.noise(wx * 0.022, wz * 0.022);
            if (variety > 0.30) {
                out[0] = Math.min(1, out[0] + 0.12 * (variety - 0.30));
                out[1] = Math.min(1, out[1] + 0.08 * (variety - 0.30));
            } else if (variety < -0.30) {
                out[0] = Math.min(1, out[0] + 0.08 * (-variety - 0.30));
                out[1] *= 0.92;
            }
        }
    },
    subColors: [
        [0.38, 0.28, 0.16, 1], // rich humus
        [0.44, 0.34, 0.20, 1],
        [0.50, 0.40, 0.28, 1],
        [0.46, 0.40, 0.34, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return { type: 'alloy', amount: 2, desc: 'Iron Ore (+2)' };
        if (h > 0.5) return { type: 'crystal', amount: 3, desc: 'Quartz (+3)' };
        if (h > -0.1) return { type: 'biomass', amount: 2, desc: 'Organic Matter (+2)' };
        return { type: 'alloy', amount: 2, desc: 'Topsoil Minerals (+2)' };
    }
};

/**
 * BIOME: VOLCANIC HIGHLAND
 * Active volcanic terrain - coal-black basalt flows cool beside rust-red
 * scoria; sulphur-yellow fumarole vents and brilliant hot-spring pools.
 */
const BIOME_VOLCANIC = {
    id: 'volcanic',
    tempRange: [0.5, 1.0],
    moistRange: [-1.0, -0.2],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const base = continent * 0.82 + detail * 0.18;
        const mtn  = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return Math.sign(base) * Math.pow(Math.abs(base), 0.70) + mtn * 0.36;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.10, 0.24, 1] }, // cold deep ocean
        { h: -0.50, c: [0.06, 0.18, 0.40, 1] }, // open ocean
        { h: -0.12, c: [0.18, 0.50, 0.62, 1] }, // hot spring / geothermal blue
        { h:  0.00, c: [0.22, 0.20, 0.16, 1] }, // black basalt shore
        { h:  0.06, c: [0.18, 0.16, 0.12, 1] }, // fresh lava rock - near black
        { h:  0.20, c: [0.24, 0.20, 0.16, 1] }, // dark basalt plain
        { h:  0.36, c: [0.38, 0.22, 0.12, 1] }, // rust-red scoria
        { h:  0.50, c: [0.48, 0.30, 0.14, 1] }, // oxidised basalt
        { h:  0.65, c: [0.40, 0.34, 0.24, 1] }, // weathered volcanic rock
        { h:  0.78, c: [0.50, 0.46, 0.40, 1] }, // grey highland
        { h:  0.90, c: [0.68, 0.66, 0.64, 1] }, // bare summit rock
        { h:  0.96, c: [0.88, 0.90, 0.92, 1] }, // crater snow
        { h:  1.00, c: [1.00, 1.00, 1.00, 1] }, // peak snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Sulphur fumarole yellow patches
        const fum = n.noise(wx * 0.14, wz * 0.14) * 0.5 + 0.5;
        if (h > 0.08 && h < 0.55 && fum > 0.74) {
            const t = Math.min(1, (fum - 0.74) * 4.5);
            out[0] = out[0]*(1-t) + 0.72*t;
            out[1] = out[1]*(1-t) + 0.68*t;
            out[2] = out[2]*(1-t) + 0.04*t;
        }
        // Fresh lava glow in lowest basalt areas
        const lava = n.noise(wx * 0.22, wz * 0.22);
        if (h > 0.02 && h < 0.18 && lava > 0.62) {
            const t = Math.min(1, (lava - 0.62) * 5);
            out[0] = out[0]*(1-t) + 0.88*t;
            out[1] = out[1]*(1-t) + 0.30*t;
            out[2] = out[2]*(1-t) + 0.02*t;
        }
    },
    subColors: [
        [0.20, 0.16, 0.12, 1], // basalt
        [0.16, 0.12, 0.10, 1],
        [0.12, 0.08, 0.06, 1],
        [0.58, 0.16, 0.04, 1], // magma vein
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return depth >= 4
            ? { type: 'titanium', amount: 2, desc: 'Volcanic Titanite (+2)' }
            : { type: 'plasma',   amount: depth >= 3 ? 4 : 2, desc: `Magma Mineral (+${depth >= 3 ? 4 : 2})` };
        if (h <= -0.3) return { type: 'crystal',  amount: 3, desc: 'Obsidian (+3)' };
        if (h <= 0.4)  return { type: 'alloy',    amount: 3, desc: 'Basalt Ore (+3)' };
        return { type: 'titanium', amount: 1, desc: 'Titanite Vein (+1)' };
    }
};

/**
 * BIOME: ARCTIC TUNDRA
 * Frozen subarctic plains - sea ice chokes the bays; permafrost scrub
 * stretches flat in muted blue-grey broken by lichen-orange outcrops.
 */
const BIOME_TUNDRA = {
    id: 'tundra',
    tempRange: [-1.0, -0.4],
    moistRange: [0.2, 1.0],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const flat  = continent * 0.55;
        const spire = Math.pow(Math.max(0, detail - 0.5), 3) * 3.5;
        const mtn   = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return flat + spire + mtn * 0.32;
    },
    _stops: [
        { h: -1.00, c: [0.06, 0.14, 0.30, 1] }, // icy deep water
        { h: -0.55, c: [0.10, 0.22, 0.44, 1] }, // cold grey-blue sea
        { h: -0.15, c: [0.60, 0.72, 0.82, 1] }, // sea ice / slush
        { h:  0.00, c: [0.82, 0.86, 0.88, 1] }, // frozen shore / ice flat
        { h:  0.06, c: [0.68, 0.72, 0.62, 1] }, // tundra scrub - muted sage
        { h:  0.20, c: [0.54, 0.58, 0.46, 1] }, // lichen-grey flat
        { h:  0.35, c: [0.52, 0.50, 0.44, 1] }, // muskeg
        { h:  0.50, c: [0.50, 0.48, 0.44, 1] }, // permafrost rubble
        { h:  0.65, c: [0.60, 0.58, 0.56, 1] }, // rocky highland
        { h:  0.80, c: [0.72, 0.72, 0.74, 1] }, // snow-dusted rock
        { h:  0.88, c: [0.88, 0.90, 0.94, 1] }, // snowfield
        { h:  1.00, c: [0.98, 0.99, 1.00, 1] }, // deep snowcap
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Lichen orange-brown patches on low tundra
        if (h > 0.04 && h < 0.45) {
            const lichen = n.noise(wx * 0.055, wz * 0.055) * 0.5 + 0.5;
            if (lichen > 0.62) {
                const t = Math.min(1, (lichen - 0.62) * 3.5);
                out[0] = out[0]*(1-t) + 0.62*t;
                out[1] = out[1]*(1-t) + 0.44*t;
                out[2] = out[2]*(1-t) + 0.18*t;
            }
        }
        // Wind-scoured snow brightening on exposed ridges
        if (h >= 0.80) {
            const wind = n.noise(wx * 0.12, wz * 0.04) * 0.5 + 0.5;
            const t    = Math.min(1, (h - 0.80) * 5 + wind * 0.3);
            out[0] = Math.min(1, out[0] + t * 0.12);
            out[1] = Math.min(1, out[1] + t * 0.10);
            out[2] = Math.min(1, out[2] + t * 0.08);
        }
    },
    subColors: [
        [0.46, 0.44, 0.40, 1], // frozen soil
        [0.38, 0.36, 0.34, 1],
        [0.30, 0.28, 0.26, 1],
        [0.24, 0.22, 0.20, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return { type: 'crystal', amount: depth >= 2 ? 3 : 1, desc: `Permafrost Mineral (+${depth >= 2 ? 3 : 1})` };
        if (h <= 0.1)  return { type: 'crystal', amount: 2, desc: 'Ice Crystal (+2)' };
        if (h <= 0.6)  return { type: 'alloy',   amount: 2, desc: 'Frozen Ore (+2)' };
        return { type: 'crystal', amount: 3, desc: 'Alpine Crystal (+3)' };
    }
};

/**
 * BIOME: TROPICAL RAINFOREST
 * Dense equatorial jungle - emerald-green canopy blanketing steaming valleys,
 * muddy river banks, vivid undergrowth, and misty cloud-covered peaks.
 */
const BIOME_ALIEN_JUNGLE = {
    id: 'alien_jungle',
    tempRange: [0.2, 1.0],
    moistRange: [0.6, 1.0],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const mtn = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return continent * 0.75 + detail * 0.20 + Math.sin(continent * Math.PI * 1.5) * 0.05 + mtn * 0.38;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.12, 0.28, 1] }, // deep sea - navy
        { h: -0.50, c: [0.06, 0.22, 0.50, 1] }, // tropical ocean - bright blue
        { h: -0.14, c: [0.14, 0.48, 0.64, 1] }, // warm shallows - teal
        { h:  0.00, c: [0.70, 0.64, 0.46, 1] }, // sand / silt shore
        { h:  0.04, c: [0.52, 0.58, 0.28, 1] }, // mangrove / wet shore
        { h:  0.14, c: [0.22, 0.50, 0.12, 1] }, // jungle floor - vivid green
        { h:  0.30, c: [0.14, 0.42, 0.08, 1] }, // dense canopy
        { h:  0.48, c: [0.10, 0.34, 0.06, 1] }, // deep jungle - dark green
        { h:  0.62, c: [0.24, 0.38, 0.14, 1] }, // misty mid-slope
        { h:  0.75, c: [0.42, 0.44, 0.36, 1] }, // upper forest / rocky
        { h:  0.86, c: [0.58, 0.58, 0.56, 1] }, // cloud-level rock
        { h:  0.94, c: [0.88, 0.90, 0.94, 1] }, // mist / snow
        { h:  1.00, c: [1.00, 1.00, 1.00, 1] }, // peak snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Dappled light variation on canopy
        if (h > 0.10 && h < 0.65) {
            const dapple = n.noise(wx * 0.045, wz * 0.045);
            out[0] += dapple * 0.04;
            out[1] += dapple * 0.06;
            out[2] += dapple * 0.02;
        }
        // Muddy river sediment in valleys
        if (h > 0.00 && h < 0.08) {
            const mud = n.noise(wx * 0.09, wz * 0.09) * 0.5 + 0.5;
            if (mud > 0.60) {
                const t = Math.min(1, (mud - 0.60) * 3);
                out[0] = out[0]*(1-t) + 0.46*t;
                out[1] = out[1]*(1-t) + 0.36*t;
                out[2] = out[2]*(1-t) + 0.18*t;
            }
        }
    },
    subColors: [
        [0.28, 0.24, 0.14, 1], // tropical red-brown soil
        [0.36, 0.28, 0.18, 1],
        [0.40, 0.32, 0.22, 1],
        [0.44, 0.36, 0.28, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return { type: 'alloy', amount: depth >= 3 ? 3 : 2, desc: `Tropical Ore (+${depth >= 3 ? 3 : 2})` };
        if (h <= -0.2) return { type: 'crystal', amount: 2, desc: 'Reef Crystal (+2)' };
        if (h <= 0.15) return { type: 'biomass', amount: 2, desc: 'Organic Matter (+2)' };
        if (h <= 0.55) return { type: 'alloy',   amount: 3, desc: 'Jungle Minerals (+3)' };
        return { type: 'crystal', amount: 2, desc: 'Mountain Ore (+2)' };
    }
};

/**
 * BIOME: DESERT CANYON
 * Sandstone mesa country - sun-bleached orange and terracotta canyon walls
 * drop into shadowed red-ochre ravines; white salt flats at the valley floor.
 */
const BIOME_CANYON = {
    id: 'canyon',
    tempRange: [0.3, 1.0],
    moistRange: [-1.0, -0.3],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const mesa = Math.min(1.0, continent * 1.3);
        const mtn  = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return mesa * 0.80 - Math.max(0, -detail * 0.20) + mtn * 0.42;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.10, 0.26, 1] }, // deep water
        { h: -0.45, c: [0.06, 0.20, 0.44, 1] }, // desert lake blue
        { h: -0.10, c: [0.24, 0.50, 0.62, 1] }, // shallow oasis
        { h:  0.00, c: [0.84, 0.78, 0.58, 1] }, // white salt flat
        { h:  0.05, c: [0.80, 0.64, 0.40, 1] }, // sandy floor
        { h:  0.18, c: [0.72, 0.48, 0.26, 1] }, // canyon floor shadow
        { h:  0.32, c: [0.78, 0.42, 0.20, 1] }, // lower sandstone stratum
        { h:  0.48, c: [0.84, 0.52, 0.24, 1] }, // mid sandstone
        { h:  0.62, c: [0.86, 0.62, 0.32, 1] }, // sunlit mesa face
        { h:  0.76, c: [0.78, 0.58, 0.38, 1] }, // mesa top
        { h:  0.88, c: [0.68, 0.58, 0.48, 1] }, // rocky highland
        { h:  0.95, c: [0.86, 0.88, 0.90, 1] }, // rare snow
        { h:  1.00, c: [0.96, 0.98, 1.00, 1] }, // peak
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Horizontal sandstone strata banding
        const strata = Math.sin(h * 22.0) * 0.04;
        out[0] = Math.min(1, Math.max(0, out[0] + strata));
        out[1] = Math.min(1, Math.max(0, out[1] + strata * 0.6));
        // Bleached rock faces in direct sunlight
        const bleach = n.noise(wx * 0.025, wz * 0.025);
        if (h > 0.50 && bleach > 0.40) {
            const t = Math.min(1, (bleach - 0.40) * 2);
            out[0] = Math.min(1, out[0] + t * 0.10);
            out[1] = Math.min(1, out[1] + t * 0.08);
            out[2] = Math.min(1, out[2] + t * 0.06);
        }
    },
    subColors: [
        [0.70, 0.44, 0.24, 1], // sandstone
        [0.58, 0.34, 0.18, 1],
        [0.48, 0.26, 0.14, 1],
        [0.38, 0.20, 0.10, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return depth >= 3 ? { type: 'crystal', amount: 3, desc: 'Sandstone Gem (+3)' }
            : { type: 'alloy', amount: 2, desc: 'Desert Iron (+2)' };
        if (h <= -0.1) return { type: 'crystal', amount: 2, desc: 'Salt Crystal (+2)' };
        if (h <= 0.7)  return { type: 'alloy',   amount: 2, desc: 'Sandstone Ore (+2)' };
        return { type: 'crystal', amount: 2, desc: 'Mesa Mineral (+2)' };
    }
};

/**
 * BIOME: OCEAN ARCHIPELAGO
 * Scattered island chain - pure deep navy between islands; brilliant turquoise
 * coral shallows; white-sand cays; lush green islets rising to basalt peaks.
 */
const BIOME_VOID_ARCHIPELAGO = {
    id: 'void_archipelago',
    tempRange: [-0.9, -0.5],
    moistRange: [-0.8, -0.2],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const base  = continent * 0.72 + detail * 0.28;
        const swell = Math.sin(continent * Math.PI * 2) * 0.12;
        const mtn   = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return base + swell + mtn * 0.40;
    },
    _stops: [
        { h: -2.00, c: [0.02, 0.06, 0.18, 1] }, // abyssal plain
        { h: -0.80, c: [0.04, 0.12, 0.32, 1] }, // deep ocean
        { h: -0.35, c: [0.06, 0.24, 0.56, 1] }, // mid ocean
        { h: -0.10, c: [0.14, 0.52, 0.74, 1] }, // coral reef shallows
        { h: -0.02, c: [0.46, 0.80, 0.82, 1] }, // breaking surf / reef crest
        { h:  0.00, c: [0.92, 0.88, 0.68, 1] }, // white-sand cay
        { h:  0.04, c: [0.82, 0.78, 0.56, 1] }, // dry sand
        { h:  0.12, c: [0.34, 0.56, 0.16, 1] }, // island grass / palms
        { h:  0.30, c: [0.20, 0.44, 0.10, 1] }, // jungle slope
        { h:  0.52, c: [0.36, 0.38, 0.28, 1] }, // rocky interior
        { h:  0.70, c: [0.54, 0.52, 0.50, 1] }, // basalt cliff
        { h:  0.88, c: [0.72, 0.72, 0.74, 1] }, // cloud-covered peak
        { h:  1.00, c: [0.94, 0.96, 1.00, 1] }, // mist / snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Coral-reef colour variation: warm orange patches
        if (h > -0.14 && h < -0.01) {
            const coral = n.noise(wx * 0.20, wz * 0.20) * 0.5 + 0.5;
            if (coral > 0.60) {
                const t = Math.min(1, (coral - 0.60) * 3.5);
                out[0] = out[0]*(1-t) + 0.72*t;
                out[1] = out[1]*(1-t) + 0.38*t;
                out[2] = out[2]*(1-t) + 0.28*t;
            }
        }
        // Snow sparkle on peaks
        if (h >= 0.88) {
            const s = n.noise(wx * 0.14, wz * 0.14) * 0.025;
            out[0] = Math.min(1, out[0] + s);
            out[1] = Math.min(1, out[1] + s);
            out[2] = Math.min(1, out[2] + s);
        }
    },
    subColors: [
        [0.28, 0.24, 0.18, 1],
        [0.22, 0.18, 0.14, 1],
        [0.16, 0.12, 0.10, 1],
        [0.10, 0.08, 0.06, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return { type: 'crystal', amount: depth >= 2 ? 4 : 2, desc: `Sea Mineral (+${depth >= 2 ? 4 : 2})` };
        if (h > 0.6)  return { type: 'alloy',   amount: 3, desc: 'Basalt Ore (+3)' };
        if (h > 0.1)  return { type: 'crystal', amount: 3, desc: 'Island Crystal (+3)' };
        return { type: 'crystal', amount: 2, desc: 'Coral Mineral (+2)' };
    }
};

/**
 * BIOME: BADLANDS
 * Eroded semi-arid scrubland - pale buff and dusty mauve buttes, iron-red
 * gully floors, scattered sagebrush. Think Painted Desert or South Dakota.
 */
const BIOME_NEON_ABYSS = {
    id: 'neon_abyss',
    tempRange: [0.1, 0.5],
    moistRange: [-0.5, 0.1],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const tower = detail > 0.55 ? Math.pow((detail - 0.55) * 2.22, 1.5) * 1.2 : 0;
        const mtn   = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return continent * 0.75 + tower * 0.25 + mtn * 0.35;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.10, 0.26, 1] }, // deep water
        { h: -0.45, c: [0.08, 0.22, 0.46, 1] }, // muddy river lake
        { h: -0.10, c: [0.26, 0.46, 0.58, 1] }, // shallow muddy water
        { h:  0.00, c: [0.76, 0.68, 0.52, 1] }, // silt flat
        { h:  0.08, c: [0.72, 0.58, 0.40, 1] }, // dried mud / clay
        { h:  0.22, c: [0.68, 0.50, 0.32, 1] }, // pale mauve earth
        { h:  0.38, c: [0.74, 0.46, 0.28, 1] }, // terracotta gully
        { h:  0.52, c: [0.80, 0.54, 0.32, 1] }, // sunlit butte
        { h:  0.66, c: [0.76, 0.58, 0.42, 1] }, // upper badland
        { h:  0.80, c: [0.64, 0.58, 0.50, 1] }, // rocky highland
        { h:  0.92, c: [0.82, 0.84, 0.86, 1] }, // high rock / light snow
        { h:  1.00, c: [0.96, 0.97, 1.00, 1] }, // snowcap
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Pale lavender-mauve mineral deposit patches
        if (h > 0.10 && h < 0.55) {
            const mineral = n.noise(wx * 0.040, wz * 0.040) * 0.5 + 0.5;
            if (mineral > 0.64) {
                const t = Math.min(1, (mineral - 0.64) * 3.5);
                out[0] = out[0]*(1-t) + (out[0]*0.90 + 0.06)*t;
                out[1] = out[1]*(1-t) + (out[1]*0.86)*t;
                out[2] = out[2]*(1-t) + (out[2] + 0.10)*t;
            }
        }
    },
    subColors: [
        [0.62, 0.44, 0.28, 1],
        [0.52, 0.36, 0.22, 1],
        [0.44, 0.30, 0.18, 1],
        [0.36, 0.24, 0.14, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return { type: 'alloy', amount: depth >= 3 ? 3 : 2, desc: `Badlands Ore (+${depth >= 3 ? 3 : 2})` };
        if (h > 0.5) return { type: 'crystal', amount: 3, desc: 'Mineral Deposit (+3)' };
        return { type: 'alloy', amount: 2, desc: 'Sediment Ore (+2)' };
    }
};

/**
 * BIOME: ALPINE ROCKY MOUNTAINS
 * High-altitude jagged peaks - dark pine forest on lower slopes gives way to
 * grey scree, exposed granite, and vast permanent snowfields at the summit.
 */
const BIOME_SHATTERED_REALM = {
    id: 'shattered_realm',
    tempRange: [-0.3, 0.2],
    moistRange: [0.2, 0.6],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const slab = continent + Math.sin(continent * Math.PI * 4) * 0.06;
        const frac = detail > 0.5 ? (detail - 0.5) * 1.8 : 0;
        const mtn  = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return slab * 0.82 + frac * 0.18 + mtn * 0.38;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.10, 0.26, 1] }, // deep cold lake
        { h: -0.40, c: [0.08, 0.22, 0.48, 1] }, // glacial lake blue
        { h: -0.10, c: [0.22, 0.50, 0.68, 1] }, // glacial melt-water
        { h:  0.00, c: [0.64, 0.62, 0.52, 1] }, // gravelly shore
        { h:  0.06, c: [0.40, 0.54, 0.22, 1] }, // alpine meadow - bright
        { h:  0.20, c: [0.26, 0.44, 0.14, 1] }, // lower pine forest
        { h:  0.38, c: [0.20, 0.36, 0.12, 1] }, // upper pine - dark
        { h:  0.54, c: [0.44, 0.42, 0.36, 1] }, // treeline scrub
        { h:  0.66, c: [0.54, 0.52, 0.48, 1] }, // scree / boulder field
        { h:  0.78, c: [0.62, 0.60, 0.58, 1] }, // exposed granite
        { h:  0.88, c: [0.82, 0.84, 0.88, 1] }, // snow-streaked rock
        { h:  0.94, c: [0.94, 0.96, 0.98, 1] }, // deep snowfield
        { h:  1.00, c: [1.00, 1.00, 1.00, 1] }, // pure summit snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Snow patches on shaded rock faces (large noise blobs)
        if (h > 0.72 && h < 0.92) {
            const shade = n.noise(wx * 0.018, wz * 0.018) * 0.5 + 0.5;
            if (shade > 0.58) {
                const t = Math.min(1, (shade - 0.58) * 4);
                out[0] = Math.min(1, out[0] + t * 0.22);
                out[1] = Math.min(1, out[1] + t * 0.22);
                out[2] = Math.min(1, out[2] + t * 0.24);
            }
        }
        // Dark shadowed cliff faces
        if (h > 0.50 && h < 0.80) {
            const shadow = n.noise(wx * 0.030, wz * 0.030);
            if (shadow < -0.35) {
                const t = Math.min(1, (-shadow - 0.35) * 3);
                out[0] *= (1 - t * 0.20);
                out[1] *= (1 - t * 0.20);
                out[2] *= (1 - t * 0.18);
            }
        }
    },
    subColors: [
        [0.48, 0.44, 0.40, 1], // granite
        [0.40, 0.36, 0.32, 1],
        [0.34, 0.30, 0.28, 1],
        [0.28, 0.24, 0.22, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return depth >= 3
            ? { type: 'titanium', amount: 3, desc: 'Titanium Ore (+3)' }
            : { type: 'alloy',    amount: 3, desc: 'Granite Ore (+3)' };
        if (h < -0.2) return { type: 'crystal', amount: 3, desc: 'Glacial Mineral (+3)' };
        return { type: 'alloy', amount: 3, desc: 'Mountain Ore (+3)' };
    }
};

/**
 * BIOME: SWAMP / WETLAND
 * Low-lying waterlogged floodplains - stagnant murky green water, black peat
 * hummocks, dense reeds and dark cypress.
 */
const BIOME_FUNGAL_CATHEDRAL = {
    id: 'fungal_cathedral',
    tempRange: [0.2, 0.6],
    moistRange: [0.3, 0.7],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const base  = continent * 0.72;
        const spore = detail > 0.55 ? Math.pow((detail - 0.55) * 2.22, 1.8) * 2.2 : 0;
        const mtn   = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return base + spore * 0.28 + mtn * 0.32;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.10, 0.22, 1] }, // deep water
        { h: -0.50, c: [0.06, 0.16, 0.30, 1] }, // dark swamp water
        { h: -0.15, c: [0.10, 0.22, 0.16, 1] }, // murky green water
        { h:  0.00, c: [0.16, 0.22, 0.12, 1] }, // waterlogged bank
        { h:  0.06, c: [0.20, 0.28, 0.12, 1] }, // peat / bog
        { h:  0.18, c: [0.18, 0.30, 0.10, 1] }, // reed beds
        { h:  0.32, c: [0.14, 0.26, 0.08, 1] }, // dark cypress forest
        { h:  0.48, c: [0.18, 0.28, 0.10, 1] }, // swamp woodland
        { h:  0.62, c: [0.28, 0.34, 0.16, 1] }, // drier forest
        { h:  0.76, c: [0.46, 0.42, 0.30, 1] }, // rocky rise
        { h:  0.88, c: [0.60, 0.58, 0.54, 1] }, // bare rock
        { h:  0.95, c: [0.84, 0.86, 0.90, 1] }, // snow
        { h:  1.00, c: [0.98, 0.99, 1.00, 1] }, // peak snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Algae bloom patches in shallow water
        if (h > -0.18 && h < 0.02) {
            const algae = n.noise(wx * 0.055, wz * 0.055) * 0.5 + 0.5;
            if (algae > 0.60) {
                const t = Math.min(1, (algae - 0.60) * 4);
                out[0] = out[0]*(1-t) + 0.18*t;
                out[1] = out[1]*(1-t) + 0.38*t;
                out[2] = out[2]*(1-t) + 0.08*t;
            }
        }
        // Dark peat patches on the bog surface
        if (h > 0.02 && h < 0.30) {
            const peat = n.noise(wx * 0.080, wz * 0.080);
            if (peat < -0.30) {
                const t = Math.min(1, (-peat - 0.30) * 2.5);
                out[0] *= (1 - t * 0.35);
                out[1] *= (1 - t * 0.30);
                out[2] *= (1 - t * 0.20);
            }
        }
    },
    subColors: [
        [0.20, 0.18, 0.12, 1], // dark peat
        [0.16, 0.14, 0.10, 1],
        [0.12, 0.10, 0.08, 1],
        [0.22, 0.20, 0.14, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return { type: 'alloy', amount: depth >= 2 ? 3 : 2, desc: `Peat Mineral (+${depth >= 2 ? 3 : 2})` };
        if (h > 0.5) return { type: 'crystal', amount: 2, desc: 'Rock Crystal (+2)' };
        if (h > 0.0) return { type: 'biomass', amount: 3, desc: 'Fungal Matter (+3)' };
        return { type: 'biomass', amount: 2, desc: 'Swamp Biomass (+2)' };
    }
};

/**
 * BIOME: MEDITERRANEAN SCRUBLAND
 * Sun-drenched hillsides of pale limestone and terracotta soil, dotted with
 * silver-green olive and maquis. Sapphire-blue sea below white-sand shores.
 */
const BIOME_ANCIENT_RUINS = {
    id: 'ancient_ruins',
    tempRange: [0.0, 0.4],
    moistRange: [-0.2, 0.3],
    heightAmp: 1.0,
    heightShape(continent, detail) {
        const platform = Math.max(0, continent * 0.75);
        const pylon    = detail > 0.6 ? Math.pow((detail - 0.6) * 2.5, 1.4) * 2.0 : 0;
        const mtn      = Math.pow(Math.max(0, detail - 0.32), 2.0) * 3.5;
        return platform + pylon * 0.25 + mtn * 0.35;
    },
    _stops: [
        { h: -1.00, c: [0.04, 0.12, 0.34, 1] }, // deep sapphire sea
        { h: -0.50, c: [0.06, 0.22, 0.56, 1] }, // open sea - vivid blue
        { h: -0.14, c: [0.14, 0.48, 0.72, 1] }, // clear coastal - bright
        { h: -0.02, c: [0.36, 0.70, 0.80, 1] }, // surf / shallows
        { h:  0.00, c: [0.88, 0.84, 0.64, 1] }, // pale sand
        { h:  0.05, c: [0.82, 0.74, 0.54, 1] }, // dry sandy shore
        { h:  0.14, c: [0.56, 0.58, 0.30, 1] }, // dry grass / maquis
        { h:  0.28, c: [0.44, 0.50, 0.22, 1] }, // olive-green scrub
        { h:  0.44, c: [0.52, 0.46, 0.28, 1] }, // terracotta hillside
        { h:  0.60, c: [0.68, 0.60, 0.44, 1] }, // pale limestone
        { h:  0.74, c: [0.72, 0.68, 0.60, 1] }, // rocky highland
        { h:  0.86, c: [0.76, 0.76, 0.78, 1] }, // bare rock
        { h:  0.94, c: [0.90, 0.92, 0.96, 1] }, // snow
        { h:  1.00, c: [1.00, 1.00, 1.00, 1] }, // peak snow
    ],
    surfaceColor(h, wx, wz, n, out) {
        _stopColor(this._stops, h, out);
        // Silver-grey shimmer on olive / maquis in the breeze
        if (h > 0.10 && h < 0.50) {
            const shimmer = n.noise(wx * 0.055, wz * 0.055) * 0.5 + 0.5;
            if (shimmer > 0.56) {
                const t = Math.min(1, (shimmer - 0.56) * 3.5);
                out[0] = out[0]*(1-t) + Math.min(1, out[0] + 0.14)*t;
                out[1] = out[1]*(1-t) + Math.min(1, out[1] + 0.14)*t;
                out[2] = out[2]*(1-t) + Math.min(1, out[2] + 0.12)*t;
            }
        }
        // Terracotta / rust soil patches on bare hillside
        if (h > 0.30 && h < 0.65) {
            const soil = n.noise(wx * 0.032, wz * 0.032);
            if (soil > 0.40) {
                const t = Math.min(1, (soil - 0.40) * 2.5);
                out[0] = Math.min(1, out[0] + t * 0.10);
                out[1] = Math.max(0, out[1] - t * 0.04);
                out[2] = Math.max(0, out[2] - t * 0.06);
            }
        }
    },
    subColors: [
        [0.60, 0.50, 0.34, 1], // limestone / marl
        [0.50, 0.42, 0.28, 1],
        [0.42, 0.34, 0.22, 1],
        [0.34, 0.26, 0.18, 1],
    ],
    resource(h, isSurface, depth) {
        if (!isSurface) return depth >= 3
            ? { type: 'crystal', amount: 3, desc: 'Limestone Gem (+3)' }
            : { type: 'alloy',   amount: 2, desc: 'Terracotta Ore (+2)' };
        if (h > 0.7) return { type: 'crystal', amount: 3, desc: 'Mountain Crystal (+3)' };
        if (h > 0.2) return { type: 'alloy',   amount: 2, desc: 'Hillside Minerals (+2)' };
        return { type: 'alloy', amount: 2, desc: 'Coastal Stone (+2)' };
    }
};
// -- Master biome list ----------------------------------------------------------
export const BIOMES = [
    BIOME_GRASSLANDS,
    BIOME_VOLCANIC,
    BIOME_TUNDRA,
    BIOME_ALIEN_JUNGLE,
    BIOME_CANYON,
    BIOME_FALLEN_CITY,
    BIOME_CYBER_GRID,
    BIOME_FLESH_HIVE,
    // -- Enhanced new biomes ----------------------------------------------------
    BIOME_VOID_ARCHIPELAGO,
    BIOME_NEON_ABYSS,
    BIOME_SHATTERED_REALM,
    BIOME_FUNGAL_CATHEDRAL,
    BIOME_ANCIENT_RUINS,
];

// -------------------------------------------------------------------------------
//  SECTION 2b � BIOME MANAGER
//
//  Replaces the linear Z-axis BIOME_PERIOD logic with a 2D multi-fractal noise
//  map using independent temperature and moisture axes. Biomes are queried by
//  finding the closest match in (temp, moisture) space and blending the top-2.
//
//  Registration API: BiomeManager.register(biome) � idempotent by id.
// -------------------------------------------------------------------------------

class BiomeManager {
    constructor() {
        /** @type {Map<string, object>} */
        this._registry = new Map();
    }

    /**
     * Register a biome definition. Idempotent: re-registering the same id
     * replaces the previous entry.
     * @param {object} biome  � must have id, tempRange, moistRange
     */
    register(biome) {
        if (!biome || !biome.id) throw new Error('[BiomeManager] biome must have an id');
        this._registry.set(biome.id, biome);
        return this;
    }

    /**
     * Query the two closest biomes and blend weight for a world position.
     * Uses multi-fractal 2D noise maps for temperature and moisture axes.
     *
     * @param {SimplexNoise2D} tempNoise   � temperature noise generator
     * @param {SimplexNoise2D} moistNoise  � moisture noise generator
     * @param {number} wx
     * @param {number} wz
     * @returns {{ biomeA: object, biomeB: object, blend: number }}
     */
    selectBiomes(tempNoise, moistNoise, wx, wz) {
        // Multi-fractal: coarse base + fine detail for organic boundaries
        // BIGGER BIOMES: ~3.5� wider transitions via lower noise frequency
        const temp  = tempNoise.fbm(wx * 0.00048, wz * 0.00048, 4, 2.0, 0.5)
                    + tempNoise.fbm(wx * 0.0022,  wz * 0.0022,  2, 2.0, 0.4) * 0.20;
        const moist = moistNoise.fbm(wx * 0.00060, wz * 0.00060, 4, 2.0, 0.5)
                    + moistNoise.fbm(wx * 0.0024,  wz * 0.0024,  2, 2.0, 0.4) * 0.20;

        // Score each biome by proximity in (temp, moist) space.
        // Score = 1 / (1 + distance�) so near-perfect matches dominate.
        let best1Score = -Infinity, best2Score = -Infinity;
        let best1 = null, best2 = null;

        for (const biome of this._registry.values()) {
            const tc = (biome.tempRange[0] + biome.tempRange[1]) * 0.5;
            const mc = (biome.moistRange[0] + biome.moistRange[1]) * 0.5;
            const dt = temp - tc, dm = moist - mc;
            const score = 1.0 / (1.0 + dt * dt * 2.0 + dm * dm * 2.0);
            if (score > best1Score) { best2 = best1; best2Score = best1Score; best1 = biome; best1Score = score; }
            else if (score > best2Score) { best2 = biome; best2Score = score; }
        }

        if (!best2) best2 = best1; // only one biome registered

        // Blend weight: how strongly does best2 compete with best1?
        const totalScore = best1Score + best2Score;
        const rawBlend = totalScore > 0 ? best2Score / totalScore : 0;
        // Apply smoothstep so transitions are gradual, not sudden
        const blend = _smoothstep(0, 1, Math.min(1, rawBlend * 2.5));

        return { biomeA: best1, biomeB: best2, blend };
    }
}

/** Singleton BiomeManager used by WorldSampler */
const _biomeManager = new BiomeManager();
for (const b of BIOMES) _biomeManager.register(b);

// Module-level scratch for WorldSampler blends (no per-call allocation)
const _scr4 = new Float32Array(4);
const _scrA = new Float32Array(4);
const _scrB = new Float32Array(4);

// -------------------------------------------------------------------------------
//  SECTION 3 � VOXEL MODIFICATION MAP  (Pillar 1: Unified Voxel Data Layer)
//
//  VoxelModificationMap stores player-placed and player-mined voxels in a
//  flat per-chunk hash map. It intentionally knows nothing about world generation
//  and has no noise dependencies. All persistence I/O is delegated to the caller.
//
//  Key format: `lx_ly_lz`  (local chunk integers, same as _voxelKey())
//  Values:  0             ? explicitly air (mined)
//           integer = 1   ? material id (built)
//           { id, hp, � } ? partially-damaged block
// -------------------------------------------------------------------------------

class VoxelModificationMap {
    constructor() {
        /** @type {Map<string, Object.<string, *>>} chunkId ? { key ? value } */
        this._data = new Map();
    }

    /** Return true if any mutation exists for this chunk */
    hasChunk(chunkId) { return this._data.has(chunkId); }

    /** Get (or lazily create) the mutations object for a chunk */
    getChunk(chunkId) {
        if (!this._data.has(chunkId)) this._data.set(chunkId, Object.create(null));
        return this._data.get(chunkId);
    }

    /** Peek at mutations without creating an entry */
    peekChunk(chunkId) { return this._data.get(chunkId); }

    /** Set a single voxel mutation */
    set(chunkId, voxelKey, value) {
        this.getChunk(chunkId)[voxelKey] = value;
    }

    /** Read a single voxel mutation. Returns undefined if absent. */
    get(chunkId, voxelKey) {
        const ch = this._data.get(chunkId);
        return ch ? ch[voxelKey] : undefined;
    }

    /** Remove all mutations associated with this chunk */
    deleteChunk(chunkId) { this._data.delete(chunkId); }

    /** Iterate all chunks that have mutations */
    entries() { return this._data.entries(); }

    /** Number of chunks with mutations */
    get size() { return this._data.size; }

    /** Clear everything */
    clear() { this._data.clear(); }

    /**
     * Serialize all modifications to a plain JSON-safe object.
     * @returns {{ [chunkId: string]: { [voxelKey: string]: * } }}
     */
    serializeModifications() {
        const out = Object.create(null);
        for (const [cid, mods] of this._data.entries()) {
            if (Object.keys(mods).length > 0) out[cid] = Object.assign(Object.create(null), mods);
        }
        return out;
    }

    /**
     * Restore modifications from a previously-serialized snapshot.
     * Existing data is fully replaced.
     * @param {object} data � return value of serializeModifications()
     */
    deserializeModifications(data) {
        this._data.clear();
        if (!data) return;
        for (const cid of Object.keys(data)) {
            const mods = Object.create(null);
            Object.assign(mods, data[cid]);
            this._data.set(cid, mods);
        }
    }
}

// -------------------------------------------------------------------------------
//  SECTION 4 � WORLD SAMPLER  (base generation only, no mutation knowledge)
//
//  PUBLIC API (unchanged for foliage system compatibility):
//    sampler.sample(wx, wz)           ? { height: int, colorFlat: Float32Array(4) }
//    sampler.sampleHeight(wx, wz)     ? int   (cheaper, collision-only)
//    sampler.getHeight(wx, wz)        ? int   (alias for foliage compat)
//    sampler.getBiomeName(wx, wz)     ? string
//    sampler.subColor(wx, wz, depth)  ? Float32Array(4) scratch
//    sampler.resourceAt(wx, wz, �)    ? { type, amount, desc }
// -------------------------------------------------------------------------------

/** Internal: combined raw height value in ~[-1,1] */
function biomeNormH(continent, detail) { return continent * 0.55 + detail * 0.45; }

export class WorldSampler {
    /**
     * @param {SimplexNoise2D} noiseH    � primary height noise
     * @param {SimplexNoise2D} noiseB    � legacy biome-selector noise (kept for compat)
     * @param {SimplexNoise2D} noiseMois � moisture noise for BiomeManager
     * @param {number} terrainHeight     � base amplitude in voxels
     * @param {number} terrainScale      � noise horizontal frequency
     */
    constructor(noiseH, noiseB, noiseMois, terrainHeight, terrainScale) {
        this._nh   = noiseH;
        this._nb   = noiseB;   // temp axis (repurposed from old biome noise)
        this._nm   = noiseMois;
        this._amp  = terrainHeight;
        this._sc   = terrainScale;
    }

    // -- Biome selection -------------------------------------------------------

    /** Returns { biomeA, biomeB, blend } via BiomeManager's 2D noise map. */
    _selectBiomes(wx, wz) {
        return _biomeManager.selectBiomes(this._nb, this._nm, wx, wz);
    }

    /** Internal noise accessor � kept for foliage/legacy callers */
    getBiomeNoise(wx, wz) {
        return this._nb.fbm(wx * 0.003, wz * 0.003, 2, 2.0, 0.5);
    }

    /**
     * Dominant biome classification for external pipeline systems (foliage hook).
     */
    getBiomeName(wx, wz) {
        const result = this._selectBiomes(wx, wz);
        return result.biomeA.id.toUpperCase().replace(/_/g, ' ');
    }

    /** Alias for foliage system compatibility */
    getHeight(wx, wz) { return this.sampleHeight(wx, wz); }

    // -- Main sampler ----------------------------------------------------------

    /**
     * Full sample: returns { height: int, colorFlat: Float32Array(4) }.
     * colorFlat is module-level scratch � copy before next call!
     */
    sample(wx, wz) {
        const sc = this._sc, amp = this._amp;
        const continent = this._nh.fbm(wx * sc * 0.12, wz * sc * 0.12, 4, 2.0, 0.5);
        const detail    = this._nh.fbm(wx * sc, wz * sc, 5, 2.1, 0.58);

        const { biomeA, biomeB, blend } = this._selectBiomes(wx, wz);

        // Crater logic
        const craterDetail = this._nb.noise(wx * 0.006, wz * 0.006);
        const isCrater = craterDetail > 0.88;
        const craterDepth = isCrater ? Math.pow((craterDetail - 0.88) * 12, 2.2) : 0;

        const hA = biomeA.heightShape(continent, detail) * biomeA.heightAmp;
        const hB = biomeB.heightShape(continent, detail) * biomeB.heightAmp;
        let h = hA + (hB - hA) * blend;
        h -= craterDepth;

        biomeA.surfaceColor(h, wx, wz, this._nh, _scrA);
        biomeB.surfaceColor(h, wx, wz, this._nh, _scrB);
        _lerpColor(_scr4, _scrA, _scrB, blend);

        return { height: Math.round(h * amp), colorFlat: _scr4 };
    }

    /**
     * Height-only sample (cheaper � skips colour computation).
     * Used by collision and physics; kept identical to original for parity.
     */
    sampleHeight(wx, wz) {
        const sc = this._sc, amp = this._amp;
        const continent = this._nh.fbm(wx * sc * 0.12, wz * sc * 0.12, 4, 2.0, 0.5);
        const detail    = this._nh.fbm(wx * sc, wz * sc, 5, 2.1, 0.58);
        const { biomeA, biomeB, blend } = this._selectBiomes(wx, wz);
        const hA = biomeA.heightShape(continent, detail) * biomeA.heightAmp;
        const hB = biomeB.heightShape(continent, detail) * biomeB.heightAmp;
        let h = hA + (hB - hA) * blend;
        const craterDetail = this._nb.noise(wx * 0.006, wz * 0.006);
        if (craterDetail > 0.88) h -= Math.pow((craterDetail - 0.88) * 12, 2.2);
        return Math.round(h * amp);
    }

    /** Sub-surface colour for a given depth layer */
    subColor(wx, wz, depthLayer) {
        const { biomeA, biomeB, blend } = this._selectBiomes(wx, wz);
        const maxD = Math.max(biomeA.subColors.length, biomeB.subColors.length) - 1;
        const di = Math.min(depthLayer - 1, maxD);
        const cA = biomeA.subColors[Math.min(di, biomeA.subColors.length - 1)];
        const cB = biomeB.subColors[Math.min(di, biomeB.subColors.length - 1)];
        _lerpColor(_scr4, cA, cB, blend);
        return _scr4;  // caller must copy before next call
    }

    /** Resource awarded when a voxel is mined at (wx, wz) */
    resourceAt(wx, wz, h, isSurface, depth) {
        const sc = this._sc;
        const continent = this._nh.fbm(wx * sc * 0.12, wz * sc * 0.12, 4, 2.0, 0.5);
        const detail    = this._nh.fbm(wx * sc, wz * sc, 5, 2.1, 0.58);
        const rawH = biomeNormH(continent, detail);
        const { biomeA, biomeB, blend } = this._selectBiomes(wx, wz);
        const biome = blend < 0.5 ? biomeA : biomeB;
        return biome.resource(rawH, isSurface, depth);
    }

    /**
     * Dominant biome at (wx,wz). Used by adaptive buffer scaling.
     * Returns { biome, heightAmp }.
     */
    dominantBiomeAt(wx, wz) {
        const { biomeA, biomeB, blend } = this._selectBiomes(wx, wz);
        return blend < 0.5 ? biomeA : biomeB;
    }
}

// -------------------------------------------------------------------------------
//  SECTION 5 � SURFACE NETS MESH BUILDER  (zero-allocation hot path)
//
//  Hybrid Surface Nets: a 1-bit `structuralMask` per node marks player-placed
//  voxels. When a Surface Nets cell vertex is averaged from crossing edges, any
//  corner flagged as structural forces the vertex to snap to the cell's integer
//  centre, bypassing the normal midpoint-averaging relaxation. This produces
//  hard 90� edges for built structures while smooth terrain flows freely.
//
//  Memory model: all scratch buffers are module-level TypedArrays.
//  Nothing is allocated inside hot loops.
//
//  Adaptive buffer scaling:
//    _SN_GRID_MAX and _SN_MAX_VERTS are computed per-chunk based on
//    terrainHeight � biomeAmplitude, then padded with a safe margin.
//    Backing TypedArrays are resized upward if needed (never shrunk).
// -------------------------------------------------------------------------------

// -- Edge / quad tables (identical to original) --------------------------------
const _SN_CX = new Uint8Array([0, 1, 1, 0, 0, 1, 1, 0]);
const _SN_CY = new Uint8Array([0, 0, 1, 1, 0, 0, 1, 1]);
const _SN_CZ = new Uint8Array([0, 0, 0, 0, 1, 1, 1, 1]);
const _SN_EDGE_AXIS = new Uint8Array([0, 1, 2]);
const _SN_EDGE_CB   = new Uint8Array([1, 3, 4]);
const _SN_QUAD_X    = new Int8Array([0, 0, 0, 0,   0, -1, 0, -1,   0, 0, -1, -1]);
const _SN_QUAD_Y    = new Int8Array([0, -1, 0, -1,  0, 0, 0, 0,    0, 0, -1, -1]);
const _SN_QUAD_Z    = new Int8Array([0, -1, 0, -1,  0, 0, -1, -1,  0, 0, 0, 0]);
const _SN_EDGES12_A = new Uint8Array([0, 1, 2, 3, 4, 5, 6, 7, 0, 1, 2, 3]);
const _SN_EDGES12_B = new Uint8Array([1, 2, 3, 0, 5, 6, 7, 4, 4, 5, 6, 7]);

// Module-level triangle scratch (positions, normals)
const _snPos0 = new Float32Array(3);
const _snPos1 = new Float32Array(3);
const _snPos2 = new Float32Array(3);
const _snEdge1 = new Float32Array(3);
const _snEdge2 = new Float32Array(3);
const _snNorm  = new Float32Array(3);
const _snColScratch = new Float32Array(4);

// -- Adaptive buffer pool ------------------------------------------------------
// These start at a safe baseline and expand if a chunk ever needs more.
// They are never shrunk, so the high-water mark stays allocated across frames.

const _SN_BASE_MAX_VERTS   = 32768;
const _SN_BASE_MAX_INDICES = 589824; // 32768 * 18

/** Mutable buffer holder � resize by replacing typed array references. */
const _snBufs = {
    maxVerts:   _SN_BASE_MAX_VERTS,
    maxIndices: _SN_BASE_MAX_INDICES,
    gridMax:    _SN_BASE_MAX_VERTS,

    posBuf:    new Float32Array(_SN_BASE_MAX_VERTS * 3),
    nrmBuf:    new Float32Array(_SN_BASE_MAX_VERTS * 3),
    colBuf:    new Float32Array(_SN_BASE_MAX_VERTS * 4),
    idxBuf:    new Uint32Array(_SN_BASE_MAX_INDICES),
    flatPos:   new Float32Array(_SN_BASE_MAX_INDICES * 3),
    flatNrm:   new Float32Array(_SN_BASE_MAX_INDICES * 3),
    flatCol:   new Float32Array(_SN_BASE_MAX_INDICES * 4),
    flatIdx:   new Uint32Array(_SN_BASE_MAX_INDICES),

    density:        new Float32Array(_SN_BASE_MAX_VERTS),
    colorGrid:      new Float32Array(_SN_BASE_MAX_VERTS * 4),
    structuralMask: new Uint8Array(_SN_BASE_MAX_VERTS),   // 1 = player-placed structural block
    vertIdx:        new Int32Array(_SN_BASE_MAX_VERTS),
};

/**
 * Ensure all scratch buffers can handle `totalNodes` nodes and `maxIndices`
 * index slots. Expands backing arrays in-place with a 1.5� growth factor.
 *
 * @param {number} totalNodes   � GSX * GSY * GSZ
 * @param {number} neededVerts  � upper bound on vertices
 * @param {number} neededIdx    � upper bound on indices (neededVerts * 18)
 */
function _ensureSNBuffers(totalNodes, neededVerts, neededIdx) {
    if (totalNodes > _snBufs.gridMax) {
        const next = Math.ceil(totalNodes * 1.5);
        _snBufs.density        = new Float32Array(next);
        _snBufs.colorGrid      = new Float32Array(next * 4);
        _snBufs.structuralMask = new Uint8Array(next);
        _snBufs.vertIdx        = new Int32Array(next);
        _snBufs.gridMax        = next;
    }
    if (neededVerts > _snBufs.maxVerts) {
        const next = Math.ceil(neededVerts * 1.5);
        _snBufs.posBuf  = new Float32Array(next * 3);
        _snBufs.nrmBuf  = new Float32Array(next * 3);
        _snBufs.colBuf  = new Float32Array(next * 4);
        _snBufs.maxVerts = next;
    }
    if (neededIdx > _snBufs.maxIndices) {
        const next = Math.ceil(neededIdx * 1.5);
        _snBufs.idxBuf  = new Uint32Array(next);
        _snBufs.flatPos = new Float32Array(next * 3);
        _snBufs.flatNrm = new Float32Array(next * 3);
        _snBufs.flatCol = new Float32Array(next * 4);
        _snBufs.flatIdx = new Uint32Array(next);
        _snBufs.maxIndices = next;
    }
}

/**
 * Bilinear interpolation of the surface height at a continuous world position.
 * Exported so voxel-foliage.js can anchor trees exactly on the slope.
 *
 * @param {WorldSampler} sampler
 * @param {number} wx   world X (can be fractional)
 * @param {number} wz   world Z (can be fractional)
 * @returns {number}    interpolated surface Y (float)
 */
export function getSurfaceHeight(sampler, wx, wz) {
    const x0 = Math.floor(wx), z0 = Math.floor(wz);
    const fx = wx - x0, fz = wz - z0;
    const h00 = sampler.sampleHeight(x0, z0);
    const h10 = sampler.sampleHeight(x0 + 1, z0);
    const h01 = sampler.sampleHeight(x0, z0 + 1);
    const h11 = sampler.sampleHeight(x0 + 1, z0 + 1);
    return h00 * (1 - fx) * (1 - fz)
        + h10 * fx * (1 - fz)
        + h01 * (1 - fx) * fz
        + h11 * fx * fz;
}

// -------------------------------------------------------------------------------
//  SECTION 6 � CHUNK DATA CACHE  (LRU-style, capped size)
// -------------------------------------------------------------------------------

class ChunkDataCache {
    constructor(max = 256) { this._m = new Map(); this._max = max; }
    has(id) { return this._m.has(id); }
    get(id) {
        if (!this._m.has(id)) return null;
        const val = this._m.get(id);
        this._m.delete(id);
        this._m.set(id, val); // re-insert at tail = most recently used
        return val;
    }
    set(id, data) {
        if (this._m.has(id)) this._m.delete(id);
        else if (this._m.size >= this._max) this._m.delete(this._m.keys().next().value);
        this._m.set(id, data);
    }
}

// -------------------------------------------------------------------------------
//  SECTION 7 � MODULE-LEVEL SCRATCH BUFFERS  (no per-frame GC)
// -------------------------------------------------------------------------------

const _wpBuf   = new Float32Array(3);
const _objWBuf = new Float32Array(3);
const _camWBuf = new Float32Array(3);
const _fwdBuf  = new Float32Array(3);
const _fwdBuf2 = new Float32Array(3);
const _qYaw    = new Float32Array(4);
const _qPitch  = new Float32Array(4);
const _qFinal  = new Float32Array(4);

/** Simple PRNG for deterministic structure placement inside chunks */
class ChunkRng {
    constructor(seed) { this._s = (seed >>> 0) || 1; }
    next() { this._s = (Math.imul(1664525, this._s) + 1013904223) >>> 0; return this._s / 0x100000000; }
    int(n) { return Math.floor(this.next() * n); }
}

/** Stable seed from world grid coords � two int16s folded together */
function _worldSeed(wx, wz) { return ((wx & 0xFFFF) | ((wz & 0xFFFF) << 16)) >>> 0; }

// -------------------------------------------------------------------------------
//  SECTION 8 � SHARED CONSTANTS & HELPERS
// -------------------------------------------------------------------------------

const _WHITE = [1, 1, 1, 1];

function _voxelKey(lx, ly, lz) { return `${lx}_${ly}_${lz}`; }
function _parseChunkId(id) { const c = id.indexOf(','); return [parseInt(id, 10), parseInt(id.slice(c + 1), 10)]; }
function _parseVoxelKey(key) { const a = key.indexOf('_'); const b = key.indexOf('_', a + 1); return [parseInt(key, 10), parseInt(key.slice(a + 1), 10), parseInt(key.slice(b + 1), 10)]; }

function _makeFlatColorAccessor(flat, cs) {
    const len = cs * cs;
    const views = new Array(len);
    for (let i = 0; i < len; i++) views[i] = flat.subarray(i * 4, i * 4 + 4);
    return views;
}

// -- Cardinal direction table for ghost-block / placement snapping -------------
const _CARDINALS = [
    [ 1, 0, 0], [-1, 0, 0],
    [ 0, 1, 0], [ 0,-1, 0],
    [ 0, 0, 1], [ 0, 0,-1],
];

/**
 * Snap a continuous hit normal to the nearest cardinal axis unit vector.
 * @param {number} nx @param {number} ny @param {number} nz
 * @returns {number[]} [cx, cy, cz] � one of the 6 cardinal directions
 */
function _snapToCardinal(nx, ny, nz) {
    let best = 0, bestDot = -Infinity;
    for (let i = 0; i < 6; i++) {
        const d = nx * _CARDINALS[i][0] + ny * _CARDINALS[i][1] + nz * _CARDINALS[i][2];
        if (d > bestDot) { bestDot = d; best = i; }
    }
    return _CARDINALS[best];
}

// -------------------------------------------------------------------------------
//  SECTION 9 � VoxelTreadmill  (Wonderland Engine Component)
// -------------------------------------------------------------------------------

export class VoxelTreadmill extends Component {
    static TypeName = 'voxel-treadmill';

    static Properties = {
        camera: Property.object(),
        /** Black plane/sphere parented to the desktop camera — shown when camera is inside a voxel. */
        voxelOcclusionObject: Property.object(),
        /** Black plane/sphere parented to the VR head — shown when camera is inside a voxel in XR. */
        voxelOcclusionObjectVR: Property.object(),
        /** Radius (voxel units) for occlusion detection. Larger = triggers earlier. Default 0.4. */
        voxelOcclusionRadius: Property.float(0.4),
        speed: Property.float(5.0),
        runSpeed: Property.float(10.0),
        chunkSize: Property.int(16),
        renderDistance: Property.int(3),
        baseVoxel: Property.object(),
        terrainHeight: Property.float(18.0),
        terrainScale: Property.float(0.018),
        /** Fallback water depth factor if WaterSurface component is absent.
         *  waterLevelY = -waterNormDepth × terrainHeight. Matches WaterSurface.waterNormDepth. */
        waterNormDepth: Property.float(0.35),
        columnDepth: Property.int(4),
        /** Absolute Y level for the unminable bedrock floor */
        floorLevel: Property.float(-20.0),
        noiseSeed: Property.int(42),
        genBudgetMs: Property.float(5.0),
        gravity: Property.float(-25.0),
        jumpForce: Property.float(10.0),
        glideGravity: Property.float(-2.0),
        stepHeight: Property.float(1.1),
        interactionDistance: Property.float(5.0),
        buildMaterialId: Property.int(1),
        enablePointerLook: Property.bool(true),
        pointerLockOnClick: Property.bool(true),
        moveOnClick: Property.bool(false),
        requireMouseDown: Property.bool(false),
        mouseButtonIndex: Property.int(0),
        sensitivity: Property.float(0.002),
        /* 3rd Person Camera settings for Morphism */
        playerRig: Property.object(),
        thirdPersonCamera: Property.object(),
        cameraDistance: Property.float(4.0),
        consumeLowestFirst: Property.bool(true),
        acceleration: Property.float(30.0),
        friction: Property.float(0.0),
        airControl: Property.float(1.0),
        doubleJumpForce: Property.float(10.0),
        useInputBridge: Property.bool(false),
        enableFortresses: Property.bool(true),
        improvedFortresses: Property.bool(true),
        /** VR head / HMD object — assign the XR camera or head node in the editor.
         *  Used to derive movement forward direction in VR instead of the desktop camera. */
        vrHead: Property.object(),
    };

    // -- Lifecycle -------------------------------------------------------------

    start() {
        if (!this.baseVoxel) { console.error('[VoxelTreadmill] baseVoxel not set!'); return; }
        if (this.voxelOcclusionObject)   this.voxelOcclusionObject.active   = false;
        if (this.voxelOcclusionObjectVR) this.voxelOcclusionObjectVR.active = false;
        this._wasInsideVoxel = false;

        // -- State -------------------------------------------------------------
        this._keys = {};
        this._inventory = { alloy: 0, biomass: 0, titanium: 0, plasma: 0, crystal: 0, shieldCells: 0, medkits: 0 };
        this._miningProgress = 0;
        this._health = 100;
        this._shield = 100;
        this._pickups = [];

        // -- World Sampler (Pillar 1: base generation only) --------------------
        this._hNoise    = new SimplexNoise2D(this.noiseSeed);
        this._mNoise    = new SimplexNoise2D(this.noiseSeed ^ 0xF00BA4);  // temperature axis
        this._moistNoise = new SimplexNoise2D(this.noiseSeed ^ 0xA1B2C3); // moisture axis
        this._sampler = new WorldSampler(
            this._hNoise, this._mNoise, this._moistNoise,
            this.terrainHeight, this.terrainScale
        );

        // -- Physics -----------------------------------------------------------
        this._velY = 0; this._velX = 0; this._velZ = 0;
        this._doubleJumped = false; this._prevJumpPressed = false;
        this._isGrounded = false;
        this._stepSmoothY = 0; // visual offset for smooth step-ups (negative, lerps to 0)
        this._vrMoveScale = 1; // set each frame by VRControls; desktop always 1
        this._isSwimming  = false;
        this._activeMorphism = 'walker';
        // Fallback water Y in case WaterSurface isn't in the scene.
        // WaterSurface computes the real value using the same formula once it hooks in.
        this._waterLevelY = -(this.waterNormDepth ?? 0.35) * this.terrainHeight;
        this._isDead = false;   // true while the death/respawn sequence is running

        // -- Chunk system ------------------------------------------------------
        this._dataCache = new ChunkDataCache(512);
        this._chunks    = new Map();
        this._genQueue  = [];
        this._genQueueSet = new Set();
        this._vrPreviewHit = null;
        this._playerCX = null;
        this._playerCZ = null;

        // -- Voxel Modification Map (Pillar 1) ---------------------------------
        this._mutations = new VoxelModificationMap();
        this._onVoxelEdit = null; // (wx, wy, wz, value) callback — set by multiplayer.js

        // -- Persistence -------------------------------------------------------
        this._db                     = null;
        this._dbWriteQueue           = new Map();
        this._dbFlushTimer           = null;
        this._pendingPositionRestore = null;  // set by loadPlayerState, consumed in update()
        this._blockPositionRestore   = false; // set by clearForJoin to suppress late IDB callbacks
        this._savedSpawnCX           = null;  // loaded from worldMeta; null = no saved world yet
        this._savedSpawnCZ           = null;
        this._dbReady                = this._initDB().then(() => this._startAutoSave());
        this._autoSaveInterval       = null;

        // -- Build materials (one per mineral, ordered by rarity) --------------
        this._buildMats = new Map([
            [1, [0.67, 0.71, 0.74, 1.0]],  // alloy    — steel grey
            [2, [0.22, 0.65, 0.19, 1.0]],  // biomass  — organic green
            [3, [0.55, 0.78, 0.97, 1.0]],  // titanium — ice blue
            [4, [0.95, 0.40, 0.05, 1.0]],  // plasma   — molten orange
            [5, [0.38, 0.96, 0.99, 1.0]],  // crystal  — prismatic cyan
        ]);

        // -- Materials ---------------------------------------------------------
        this._matCache = new Map();
        try {
            const Ctor = this.engine.materials.getTemplate('Phong Opaque');
            this._matMake = (rgba) => { const m = new Ctor(); m.setDiffuseColor(rgba); return m; };
        } catch (_) {
            const mc = this.baseVoxel.getComponent(MeshComponent);
            const base = mc && mc.material ? mc.material : null;
            if (!base) { console.error('[VoxelTreadmill] no material available'); return; }
            this._matMake = (rgba) => {
                const m = base.clone();
                if (typeof m.setDiffuseColor === 'function') m.setDiffuseColor(rgba);
                else m.diffuseColor = rgba;
                return m;
            };
        }
        const mc = this.baseVoxel.getComponent(MeshComponent);
        this._baseVoxelMaterial = mc ? mc.material : null;

        // -- Selection highlight -----------------------------------------------
        this.baseVoxel.active = false;
        this._highlight = this.baseVoxel.clone();
        this._highlight.active = false;
        this._highlight.setScalingLocal([0.5, 0.5, 0.5]);
        this._highlightMat = null;
        this._highlightLastMatId = -1;
        try {
            const hmc = this._highlight.getComponent(MeshComponent);
            if (hmc) {
                const baseColor = this._buildMats.get(this.buildMaterialId) || [0.9, 0.9, 0.9, 1.0];
                this._highlightMat = this._getMat([
                    Math.min(1, baseColor[0] * 0.85 + 0.18),
                    Math.min(1, baseColor[1] * 0.85 + 0.18),
                    Math.min(1, baseColor[2] * 0.85 + 0.18),
                    1.0
                ]);
                hmc.material = this._highlightMat;
                this._highlightLastMatId = this.buildMaterialId;
            }
        } catch (_) { this._highlightMat = null; }
        this._highlightMeshComp = (() => { try { return this._highlight.getComponent(MeshComponent); } catch (_) { return null; } })();
        this._isTargetingMineable = false;
        this._suppressNextHighlight = false;
        this._rmbHeld = false;

        // -- Camera look -------------------------------------------------------
        this._lookYaw = 0; this._lookPitch = 0;
        this._pointerLocked = false;
        this._mouseDown = false; this._mouseButton = -1;

        // -- Input -------------------------------------------------------------
        this._canvas = (this.engine && this.engine.canvas) || document.querySelector('canvas') || document.body;
        this._registerInputListeners();

        // -- Expose global bridge ----------------------------------------------
        try { window.VoxelTreadmill = this; } catch (_) { }

        // Park the treadmill high above terrain so the player is never inside
        // a voxel during the lobby. _startRun will reposition to the actual spawn.
        try { this.object.setPositionWorld([0, -100, 0]); } catch (_) { }

        try {
            this._applyHitAtWorld = (pos, damage = 1, shooterId = null) => {
                try {
                    if (!pos) return false;
                    const x = (typeof pos.x === 'number') ? pos.x : pos[0];
                    const y = (typeof pos.y === 'number') ? pos.y : pos[1];
                    const z = (typeof pos.z === 'number') ? pos.z : pos[2];
                    if (![x, y, z].every(v => typeof v === 'number' && isFinite(v))) return false;
                    return this._applyDamageToVoxel(Math.floor(x), Math.floor(y), Math.floor(z), damage, shooterId);
                } catch (e) { return false; }
            };
        } catch (_) { }
    }

    // -- Input registration ----------------------------------------------------

    _registerInputListeners() {
        document.addEventListener('pointerlockchange', () => {
            this._pointerLocked = document.pointerLockElement === this._canvas;
        });

        if (!this.useInputBridge) {
            this._boundWheel = (e) => {
                let id = this.buildMaterialId + (e.deltaY > 0 ? 1 : -1);
                if (id < 1) id = 5; if (id > 5) id = 1;
                this.buildMaterialId = id;
                if (window.GameInterface) window.GameInterface.updateActiveBlueprint(id);
            };
            this._onKeyDown = (e) => {
                const k = e.key.toLowerCase();
                this._keys[k] = true;
                if (k === 'f') this._onBuild();
                if (k === 'l') { try { this._canvas.requestPointerLock(); } catch (_) { } }
                if (k === 'delete') this.clearAllData();
                if (k >= '1' && k <= '5') {
                    const id = parseInt(k);
                    if (this._buildMats.has(id)) {
                        this.buildMaterialId = id;
                        if (window.GameInterface) window.GameInterface.updateActiveBlueprint(id);
                    }
                }
            };
            this._onKeyUp = (e) => { this._keys[e.key.toLowerCase()] = false; };
            this._onMouseDown = (e) => {
                this._mouseDown = true; this._mouseButton = e.button;
                if (e.button === 2) { this._rmbHeld = true; }
                if (this.pointerLockOnClick && this._canvas && !this._pointerLocked) {
                    try { this._canvas.requestPointerLock(); } catch (_) { }
                }
            };
            this._onMouseUp = (e) => {
                if (e.button === 2) {
                    this._rmbHeld = false;
                    // Only build here when pointer is NOT locked — player-input.js owns
                    // the build trigger in pointer-lock mode to avoid double resource deduction.
                    if (!this._pointerLocked) { try { this._onBuild(); } catch (_) { } }
                }
                this._mouseDown = false; this._mouseButton = -1;
            };
            this._onMouseMove = (e) => {
                if (!this.enablePointerLook || this.useInputBridge) return;
                let allow = this._pointerLocked
                    || (this.moveOnClick && this._mouseDown && this._mouseButton === this.mouseButtonIndex)
                    || (!this.moveOnClick && !this.requireMouseDown)
                    || (this.requireMouseDown && this._mouseDown);
                if (!allow) return;
                const mx = e.movementX || 0, my = e.movementY || 0;
                if (Math.abs(mx) < 0.001 && Math.abs(my) < 0.001) return;
                const sens = this.sensitivity || 0.002;
                this._lookYaw -= mx * sens;
                this._lookPitch -= my * sens;
                const maxP = Math.PI * 0.49;
                this._lookPitch = Math.max(-maxP, Math.min(maxP, this._lookPitch));
                this._applyCameraRotation();
            };

            // Only register wheel here when PlayerInput is absent; PlayerInput handles it when present
            if (!window.PlayerInput) window.addEventListener('wheel', this._boundWheel);
            window.addEventListener('keydown', this._onKeyDown);
            window.addEventListener('keyup', this._onKeyUp);
            this._canvas.addEventListener('mousedown', this._onMouseDown);
            window.addEventListener('mouseup', this._onMouseUp);
            window.addEventListener('mousemove', this._onMouseMove);
            window.addEventListener('contextmenu', (e) => e.preventDefault());
        }
    }

    // -- Input bridge API ------------------------------------------------------

    setKeys(keys) { if (keys) this._keys = Object.assign({}, keys); }
    /** VRControls calls this every frame with the normalized joystick magnitude [0..1]. */
    setVRMoveScale(s) { this._vrMoveScale = s; }
    pullBuild() { this._onBuild(); }
    /** Build from an explicit world-space origin + direction (used by VR/tools) */
    pullBuildFrom(origin, dir) {
        const hit = this._raycastFrom(origin, dir);
        this._onBuild(hit);
    }
    pullMine(damage = 1) {
        const hit = this._raycastCenter();
        if (!hit) return false;
        const cs = this.chunkSize;
        const [cx, cz] = _parseChunkId(hit.chunkId);
        return this._applyDamageToVoxel(cx * cs + hit.lx, hit.ly, cz * cs + hit.lz, damage, null);
    }
    /**
     * Mine from an explicit world-space origin + direction (used by VR hand controllers).
     * origin and dir are plain arrays or Float32Arrays [x, y, z]; dir need not be normalised.
     */
    pullMineFrom(origin, dir, damage = 1) {
        const hit = this._raycastFrom(origin, dir);
        if (!hit) return false;
        const cs = this.chunkSize;
        const [cx, cz] = _parseChunkId(hit.chunkId);
        return this._applyDamageToVoxel(cx * cs + hit.lx, hit.ly, cz * cs + hit.lz, damage, null);
    }
    setMouseState(ms) {
        if (!ms) return;
        if (ms.movementX || ms.movementY) {
            const s = this.sensitivity || 0.002;
            this._lookYaw -= (ms.movementX || 0) * s;
            this._lookPitch -= (ms.movementY || 0) * s;
            const maxP = Math.PI * 0.49;
            this._lookPitch = Math.max(-maxP, Math.min(maxP, this._lookPitch));
            this._applyCameraRotation();
        }
        if (typeof ms.down === 'boolean') this._mouseDown = !!ms.down;
        if (typeof ms.button === 'number') this._mouseButton = ms.button;
        if (this._mouseButton === 2) this._rmbHeld = this._mouseDown; else if (!this._mouseDown) this._rmbHeld = false;
        if (typeof ms.pointerLocked === 'boolean') this._pointerLocked = !!ms.pointerLocked;
    }

    // -- Update loop -----------------------------------------------------------

    update(dt) {
        // Always drain the chunk build queue so terrain is ready before play starts.
        if (this._baseVoxelMaterial) this._drainQueue();

        if (window.GameState !== 'playing') return;
        if (!this._baseVoxelMaterial) return;

        // -- Deferred position restore (applied once chunks exist) -------------
        // We wait until at least one chunk is built so the restore doesn't
        // destroy the initial chunk set before it has rendered even one frame.
        if (this._pendingPositionRestore && this._chunks.size > 0) {
            const pr = this._pendingPositionRestore;
            this._pendingPositionRestore = null;
            try {
                this.object.getPositionWorld(_wpBuf);
                _wpBuf[0] = -pr.x; _wpBuf[1] = -pr.y; _wpBuf[2] = -pr.z;
                this.object.setPositionWorld(_wpBuf);
                // Force immediate chunk update at the restored position
                const pcx = Math.floor(pr.x / this.chunkSize);
                const pcz = Math.floor(pr.z / this.chunkSize);
                this._playerCX = null; this._playerCZ = null; // force re-trigger
                this._updateWorld(pcx, pcz);
            } catch (_) {}
        }

        this._updatePickups(dt);
        if (!this._isDead) this._updateMovement(dt);
        
        // Handle Camera Perspective based on Morphism
        this._updateCameraPerspective(dt);

        this._updateVoxelOcclusion();

        this._drainQueue();
        this._updateHighlight();

        // Check for player death
        if (!this._isDead && this._health <= 0) {
            this._isDead = true;
            // Call endRun — game-loop handles cleanup, lighting reset, and playerDie().
            try { window.GameInterface?.endRun?.(); } catch (_) {}
        }

        if (window.GameInterface) window.GameInterface.updateStats(this._health, this._shield);
    }

    /** Set the player morphism and adjust camera/physics */
    setMorphism(type) {
        const valid = ['walker', 'swimmer', 'flyer', 'roller', 'climber', 'ghost', 'titan'];
        if (valid.includes(type)) {
            this._activeMorphism = type;
        }
    }

    // -- Movement --------------------------------------------------------------

    _updateMovement(dt) {
        this.object.getPositionWorld(_wpBuf);

        const distToCenter = Math.hypot(_wpBuf[0], _wpBuf[2]);
        let localGravity = this.gravity;
        const nowInGravity = distToCenter < 30;
        if (nowInGravity) {
            localGravity = this.gravity * 0.25;
            if (!this._inGravityZone && window.GameInterface)
                window.GameInterface.pushConsoleLog('GRAVITY ANOMALY: REDUCED WEIGHT DETECTED');
        }
        this._inGravityZone = nowInGravity;

        let lx = -_wpBuf[0], ly = -_wpBuf[1] - this._stepSmoothY, lz = -_wpBuf[2];

        const keys = this._keys;
        let moveX = 0, moveZ = 0;
        if (keys['w'] || keys['arrowup'])    moveZ += 1;
        if (keys['s'] || keys['arrowdown'])  moveZ -= 1;
        if (keys['a'] || keys['arrowleft'])  moveX += 1;
        if (keys['d'] || keys['arrowright']) moveX -= 1;

        this._getForward(_fwdBuf);
        _fwdBuf[1] = 0;
        vec3.normalize(_fwdBuf, _fwdBuf);
        const rfx = _fwdBuf[2], rfz = -_fwdBuf[0];
        let wishX = _fwdBuf[0] * moveZ + rfx * moveX;
        let wishZ = _fwdBuf[2] * moveZ + rfz * moveX;
        const len = Math.hypot(wishX, wishZ);
        if (len > 0.0001) { wishX /= len; wishZ /= len; }

        // -- Morphism Physics Modifiers ----------------------------------------
        let currentSpeed = keys['shift'] ? this.runSpeed : this.speed;
        let accel = this._isGrounded ? this.acceleration : this.acceleration * this.airControl;
        let morphGravity = localGravity;
        let morphFriction = this.friction;

        if (this._activeMorphism === 'swimmer') { morphGravity *= 0.1; currentSpeed *= 1.5; }
        if (this._activeMorphism === 'flyer' && len > 0.1) { morphGravity = this.glideGravity; currentSpeed *= 2.0; }
        if (this._activeMorphism === 'roller') { morphFriction = 0.5; accel *= 2.0; currentSpeed *= 1.8; }
        if (this._activeMorphism === 'titan') { currentSpeed *= 0.6; morphGravity *= 1.5; }

        // VR: scale speed by joystick magnitude for proportional feel; apply friction so
        // player stops naturally when stick is released instead of sliding indefinitely.
        const _isVR = !!(this.engine.xr?.session);
        if (_isVR) {
            currentSpeed *= Math.max(0.15, this._vrMoveScale ?? 1);
            if (len < 0.001) morphFriction = Math.max(morphFriction, 8);
        }

        // -- Water / Swimming -------------------------------------------------------
        // _waterLevelY is computed once in start() from waterNormDepth × terrainHeight.
        // WaterSurface reads the same value in _hook(), so plane and swim zone are identical.
        const _WATER_Y   = this._waterLevelY;
        const _SWIM_SURF = _WATER_Y + 0.1;   // float height: just above water plane
        const inWater    = ly < _WATER_Y + 1.0;
        if (inWater) {
            currentSpeed  = this.speed * 0.35; // slo-mo — shift dives, doesn't sprint
            accel        *= 0.55;              // sluggish acceleration
            morphGravity  = 0;                 // replaced by buoyancy spring below
        }
        this._isSwimming = inWater;

        const maxDelta = accel * dt;
        this._velX += Math.max(-maxDelta, Math.min(maxDelta, wishX * currentSpeed - this._velX));
        this._velZ += Math.max(-maxDelta, Math.min(maxDelta, wishZ * currentSpeed - this._velZ));
        if (len < 0.001) { const fr = Math.max(0, 1 - Math.min(1, morphFriction * dt)); this._velX *= fr; this._velZ *= fr; }
        // Water drag applies even while moving (viscous resistance)
        if (inWater) { const wd = Math.max(0, 1.0 - 5.0 * dt); this._velX *= wd; this._velZ *= wd; }

        // -- Collision Tuning -------------------------------------------------
        let pR = (this._activeMorphism === 'ghost') ? 0.15 : (this._activeMorphism === 'titan' ? 0.8 : 0.35);
        let climbStep = (this._activeMorphism === 'climber') ? 2.5 : this.stepHeight;

        const nextX = lx + this._velX * dt;
        const nextZ = lz + this._velZ * dt;
        const pH = (this._activeMorphism === 'ghost') ? 0.5 : 1.8;
        const collide = (tx, ty, tz) => {
            // Start probing just above the floor voxel (floor(ty)+1) rather than a
            // fixed +0.05. The sub-voxel snap (+0.75) means floor(ty+0.05)==floor(ty),
            // which is the surface voxel itself — causing false wall-hits every frame
            // on flat terrain and triggering spurious step-ups (the bump).
            const hStart = Math.min(Math.floor(ty) + 1 - ty + 0.01, pH - 0.01);
            for (let h = hStart; h < pH; h += 0.5) {
                if (this._isSolid(tx - pR, ty + h, tz - pR) || this._isSolid(tx + pR, ty + h, tz - pR) ||
                    this._isSolid(tx - pR, ty + h, tz + pR) || this._isSolid(tx + pR, ty + h, tz + pR)) return true;
            }
            return false;
        };
        let steppedUp = false;
        if (!collide(nextX, ly, lz)) { lx = nextX; }
        else if (!collide(nextX, ly + climbStep, lz) && !collide(nextX, ly + climbStep + 0.1, lz)) { lx = nextX; ly += climbStep; this._stepSmoothY -= climbStep; steppedUp = true; }
        else { this._velX = 0; }
        if (!collide(lx, ly, nextZ)) { lz = nextZ; }
        else if (!steppedUp && !collide(lx, ly + climbStep, nextZ) && !collide(lx, ly + climbStep + 0.1, nextZ)) { lz = nextZ; ly += climbStep; this._stepSmoothY -= climbStep; }
        else { this._velZ = 0; }

        this._isGrounded = false;
        outer: for (let ox = -pR; ox <= pR; ox += pR * 2)
            for (let oz = -pR; oz <= pR; oz += pR * 2)
                if (this._isSolid(lx + ox, ly - 0.01, lz + oz)) { this._isGrounded = true; break outer; }

        const jumpPressed = !!keys[' '];
        const justJumped  = jumpPressed && !this._prevJumpPressed;
        if (inWater) {
            // -- Swimming vertical physics ------------------------------------------
            // Buoyancy automatically floats player to surface
            // Shift (optional) → dive down; in VR shift = sprint, so ignore it here
            const divingDown = !_isVR && !!keys['shift'];
            const surfGap    = _SWIM_SURF - ly; // positive when player needs to rise
            if (divingDown) {
                this._velY = Math.max(this._velY - 14.0 * dt, -7.0);
            } else {
                // Automatic buoyancy spring — always pulls toward surface
                this._velY += (surfGap * 4.5 - this._velY * 3.0) * Math.min(1.0, 4.0 * dt);
            }
            this._velY    *= Math.max(0.0, 1.0 - 3.5 * dt); // water drag on Y
            this._isGrounded   = false;
            this._doubleJumped = false;
            this._stepSmoothY  = 0; // no step-up visual while swimming
        } else if (this._isGrounded) {
            this._doubleJumped = false; this._velY = Math.max(0, this._velY);
            if (justJumped) { this._velY = this.jumpForce; this._isGrounded = false; }
        } else {
            if (justJumped && !this._doubleJumped) {
                this._velY = this.doubleJumpForce || this.jumpForce; this._doubleJumped = true;
                this._getForward(_fwdBuf2); this._velX += _fwdBuf2[0] * 2.0; this._velZ += _fwdBuf2[2] * 2.0;
            }
            this._velY += (jumpPressed && this._velY < 0 ? this.glideGravity : morphGravity) * dt;
            // Terminal velocity — prevents tunneling through voxel columns on
            // low framerates, including the long drop from spawn-in-air.
            if (this._velY < -30) this._velY = -30;
        }
        ly += this._velY * dt;

        if (this._velY <= 0) {
            let hitBlock = null;
            outer2: for (let ox = -pR; ox <= pR; ox += pR * 2)
                for (let oz = -pR; oz <= pR; oz += pR * 2) {
                    const b = this._getBlockAt(Math.floor(lx + ox), Math.floor(ly), Math.floor(lz + oz));
                    if (b !== null) { hitBlock = b; break outer2; }
                }
            if (hitBlock) {
                const prevLy = ly;
                if (!hitBlock.built) {
                    // Terrain voxel — snap to the smooth Surface Nets mesh surface instead of
                    // the integer voxel top. getSurfaceHeight returns the last solid voxel index;
                    // the SN mesh crosses halfway between solid and air, so add 0.75.
                    // Cap bilinear to Math.floor(ly) so interpolation across a height boundary
                    // never pushes the player above the solid voxel at their position, which
                    // would break the grounded check and cause oscillation (bumping on flat terrain).
                    const rawSurf = getSurfaceHeight(this._sampler, lx, lz);
                    const surfH = Math.min(rawSurf, Math.floor(ly));
                    ly = surfH + 0.75;
                } else {
                    // Structural / player-placed voxel — hard integer snap for crisp ledges.
                    ly = Math.floor(ly) + 1;
                }
                // Absorb any snap overshoot into _stepSmoothY so the visual stays
                // continuous — prevents the hop when a step-up is followed by a floor snap.
                this._stepSmoothY = Math.min(0, this._stepSmoothY - (ly - prevLy));
                this._velY = 0; this._isGrounded = true;
            }
        } else {
            if (this._isSolid(lx, ly + pH, lz)) { this._velY = 0; ly = Math.floor(ly + pH) - pH; }
        }

        // Smooth step-up: lerp the visual offset back to 0 at the player's current horizontal
        // speed so the animation feels proportional to walk/run pace, not a fixed snap.
        const hSpeed = Math.hypot(this._velX, this._velZ);
        const stepSpeed = Math.max(hSpeed, (this.speed || 4) * 0.5);
        if (this._stepSmoothY < 0) {
            this._stepSmoothY = Math.min(0, this._stepSmoothY + stepSpeed * dt);
        }
        _wpBuf[0] = -lx; _wpBuf[1] = -(ly + this._stepSmoothY); _wpBuf[2] = -lz;
        this.object.setPositionWorld(_wpBuf);
        this._prevJumpPressed = jumpPressed;

        const pcx = Math.floor(lx / this.chunkSize);
        const pcz = Math.floor(lz / this.chunkSize);
        if (pcx !== this._playerCX || pcz !== this._playerCZ) {
            this._playerCX = pcx; this._playerCZ = pcz;
            this._updateWorld(pcx, pcz);
        }
    }

    _updateVoxelOcclusion() {
        // _stepSmoothY is negative while a step-up animation is in progress — skip then.
        if ((this._stepSmoothY || 0) < -0.05) {
            if (this._wasInsideVoxel) {
                this._wasInsideVoxel = false;
                const isVR = !!(this.engine.xr && this.engine.xr.session);
                if (this.voxelOcclusionObject)   this.voxelOcclusionObject.active   = false;
                if (this.voxelOcclusionObjectVR) this.voxelOcclusionObjectVR.active = false;
            }
            return;
        }

        const isVR = !!(this.engine.xr && this.engine.xr.session);
        const probe = isVR ? this.voxelOcclusionObjectVR : this.voxelOcclusionObject;
        if (!probe) return;

        probe.getPositionWorld(_camWBuf);
        this.object.getPositionWorld(_objWBuf);
        const gx = -_objWBuf[0] + _camWBuf[0];
        const gy = -_objWBuf[1] + _camWBuf[1];
        const gz = -_objWBuf[2] + _camWBuf[2];
        const lx = Math.floor(gx), ly = Math.floor(gy), lz = Math.floor(gz);
        const fracX = gx - lx, fracZ = gz - lz;

        // Check at ly+1: filters equal-height neighbours (same-level terrain would fire at ly
        // because physics Y ≈ surfY, so _isSolid at ly is always true for equal terrain).
        // Only columns taller than the player's current surface trigger.
        const wy = ly + 1;
        const r = this.voxelOcclusionRadius || 0.4;
        const NEAR2 = r * r;
        let inside = this._isSolid(lx, wy, lz);
        for (let dx = -1; dx <= 1 && !inside; dx++) {
            for (let dz = -1; dz <= 1 && !inside; dz++) {
                if (dx === 0 && dz === 0) continue;
                const ex = dx > 0 ? (1 - fracX) : dx < 0 ? fracX : 0;
                const ez = dz > 0 ? (1 - fracZ) : dz < 0 ? fracZ : 0;
                // Only count a neighboring voxel as occluding if the space at the player's
                // floor level (ly) in that column is air — a true overhang reaching over
                // the player. Cliff faces are solid at both ly and ly+1 and should not trigger.
                if (ex * ex + ez * ez < NEAR2 && !this._isSolid(lx + dx, ly, lz + dz))
                    inside = this._isSolid(lx + dx, wy, lz + dz);
            }
        }

        if (inside !== this._wasInsideVoxel) {
            this._wasInsideVoxel = inside;
            if (this.voxelOcclusionObject)   this.voxelOcclusionObject.active   = inside && !isVR;
            if (this.voxelOcclusionObjectVR) this.voxelOcclusionObjectVR.active = inside && isVR;
        }
    }

    /** Toggle between 1st person and 3rd person based on Morphism */
    _updateCameraPerspective(dt) {
        const isVR = !!(this.engine.xr && this.engine.xr.session);
        const isStandard = this._activeMorphism === 'walker';

        if (isVR) {
            if (!this.playerRig) return;
            // In VR, we reposition the Rig to avoid breaking eye-hand tracking
            if (isStandard) {
                this.playerRig.setPositionLocal([0, 0, 0]);
            } else {
                const dist = this.cameraDistance;
                // Move rig up and back to see the morphed avatar
                this.playerRig.setPositionLocal([0, 2.0, dist]);
            }
            if (this.thirdPersonCamera) this.thirdPersonCamera.active = false;
            return;
        }

        if (!this.thirdPersonCamera || !this.camera) return;

        const targetCam = isStandard ? this.camera : this.thirdPersonCamera;
        const hiddenCam = isStandard ? this.thirdPersonCamera : this.camera;

        targetCam.active = true;
        hiddenCam.active = false;

        if (!isStandard) {
            const dist = this.cameraDistance;
            // Simple fixed follow offset for 3rd person
            this.thirdPersonCamera.setPositionLocal([0, 2.0, dist]);
        }
    }

    // -- Pickup system ---------------------------------------------------------

    _updatePickups(dt) {
        if (!this._pickups.length) return;
        for (let i = this._pickups.length - 1; i >= 0; i--) {
            const p = this._pickups[i];
            p.t += dt / p.duration;
            if (p.t >= 1.0) {
                try {
                    this._inventory[p.type] = (this._inventory[p.type] || 0) + p.amount;
                    this._miningProgress = (this._miningProgress || 0) + (p.progress || 1);
                    if (window.GameInterface) {
                        window.GameInterface.updateResources(this._inventory.alloy, this._inventory.biomass, this._inventory.titanium, this._inventory.plasma, this._inventory.crystal);
                        window.GameInterface.updateTargetProgress(this._miningProgress, 20);
                        window.GameInterface.pushConsoleLog(`MINED: ${p.desc}`);
                        window.GameInterface.updateShieldCells?.(this._inventory.shieldCells || 0);
                        window.GameInterface.updateMedkits?.(this._inventory.medkits || 0);
                        window.GameInterface.refreshCraftPanel?.();
                    }
                } catch (_) { }
                this._pickups.splice(i, 1);
            }
        }
    }

    _spawnPickup(type, amount, voxelPos, desc = '', progress = 1) {
        if (!voxelPos || voxelPos.length < 3) return;
        const start = [-voxelPos[0], -voxelPos[1], -voxelPos[2]];
        const end = [0, 0, 0];
        (this.camera && typeof this.camera.getPositionWorld === 'function')
            ? this.camera.getPositionWorld(end) : this.object.getPositionWorld(end);
        const dx = end[0] - start[0], dy = end[1] - start[1], dz = end[2] - start[2];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const duration = Math.min(Math.max(0.4, dist / 40), 1.6);
        this._pickups.push({ type, amount, start, end, t: 0, duration, desc, progress });
    }

    // -- Highlight -------------------------------------------------------------

    _updateHighlight() {
        if (!this._highlight) return;
        const hit = this._vrPreviewHit || this._raycastCenter();
        const hasHit = !!hit;
        if (hasHit !== this._isTargetingMineable) {
            this._isTargetingMineable = hasHit;
            if (window.GameInterface) window.GameInterface.setCrosshairActive(hasHit);
        }
        if (!hasHit) { this._highlight.active = false; return; }

        this.object.getPositionWorld(_objWBuf);
        const [cx, cz] = _parseChunkId(hit.chunkId);
        const cs = this.chunkSize;
        const nx = hit.normal ? hit.normal.x : 0, ny = hit.normal ? hit.normal.y : 0, nz = hit.normal ? hit.normal.z : 0;
        const testWx = cx * cs + hit.lx + nx, testWy = hit.ly + ny, testWz = cz * cs + hit.lz + nz;
        let targetWx, targetWy, targetWz, isPlaceable = false;
        try {
            if (this._getBlockAt(testWx, testWy, testWz) === null) { targetWx = testWx; targetWy = testWy; targetWz = testWz; isPlaceable = true; }
            else { targetWx = cx * cs + hit.lx; targetWy = hit.ly; targetWz = cz * cs + hit.lz; }
        } catch (_) { targetWx = cx * cs + hit.lx; targetWy = hit.ly; targetWz = cz * cs + hit.lz; }

        this._highlight.setPositionWorld([_objWBuf[0] + targetWx, _objWBuf[1] + targetWy, _objWBuf[2] + targetWz]);
        const showForBuild = isPlaceable && (this._rmbHeld || (!this._pointerLocked && this._mouseButton === 2) || !!this._vrPreviewHit);
        if (this._suppressNextHighlight) { this._highlight.active = false; this._suppressNextHighlight = false; }
        else { this._highlight.active = !!showForBuild; }

        // Refresh tint when player switches material
        if (showForBuild && this._highlightMeshComp && this._highlightLastMatId !== this.buildMaterialId) {
            try {
                const baseColor = this._buildMats.get(this.buildMaterialId) || [0.9, 0.9, 0.9, 1.0];
                const tinted = this._getMat([
                    Math.min(1, baseColor[0] * 0.85 + 0.18),
                    Math.min(1, baseColor[1] * 0.85 + 0.18),
                    Math.min(1, baseColor[2] * 0.85 + 0.18),
                    1.0
                ]);
                this._highlightMeshComp.material = tinted;
                this._highlightLastMatId = this.buildMaterialId;
            } catch (_) {}
        }
    }

    // -- World streaming -------------------------------------------------------

    _updateWorld(pcx, pcz) {
        const rd = this.renderDistance;
        const keep = new Set(), todo = [];
        for (let dx = -rd; dx <= rd; dx++) {
            for (let dz = -rd; dz <= rd; dz++) {
                const cx = pcx + dx, cz = pcz + dz;
                const id = `${cx},${cz}`;
                keep.add(id);
                if (!this._chunks.has(id) && !this._genQueueSet.has(id)) todo.push({ cx, cz, id, pri: dx * dx + dz * dz });
            }
        }
        if (this._genQueue.length) {
            const pruned = [];
            for (const q of this._genQueue) { if (keep.has(q.id)) pruned.push(q); else this._genQueueSet.delete(q.id); }
            this._genQueue = pruned;
        }
        todo.sort((a, b) => a.pri - b.pri);
        for (const t of todo) { this._genQueue.push(t); this._genQueueSet.add(t.id); }
        for (const [id, chunk] of this._chunks) { if (!keep.has(id)) this._destroyChunk(id, chunk); }
    }

    _drainQueue() {
        const t0 = performance.now(), budget = this.genBudgetMs;
        while (this._genQueue.length) {
            const item = this._genQueue.shift();
            this._genQueueSet.delete(item.id);
            if (!this._chunks.has(item.id)) this._buildChunk(item.cx, item.cz, item.id);
            if (performance.now() - t0 >= budget) break;
        }
    }

    // -- Chunk build -----------------------------------------------------------

    _buildChunk(cx, cz, id) {
        const cs = this.chunkSize;
        const data = this._dataCache.get(id) || this._computeChunkData(cx, cz);
        this._dataCache.set(id, data);
        const root = this.engine.scene.addObject(this.object);
        root.setPositionLocal([cx * cs, 0, cz * cs]);
        const meshComp = root.addComponent('mesh');
        meshComp.material = this._baseVoxelMaterial;
        this._chunks.set(id, { root, mesh: null });

        // Fix: Use a dedicated flag to track if procedural gen has run, 
        // so that early network mutations don't block structure spawning.
        if (!data._procGenDone) {
            data._procGenDone = true;
            const rng = new ChunkRng(_worldSeed(cx, cz) ^ this.noiseSeed);
            const prob = rng.next();
            if (this.enableFortresses) {
                if (prob < 0.005) {
                    if (this.improvedFortresses) this._generateFortressImproved(cx, cz, id, rng);
                    else this._generateFortress(cx, cz, id, rng);
                } else if (prob < 0.02) {
                    this._generateBunker(cx, cz, id, rng, data.heights);
                }
            } else {
                if (prob < 0.02) this._generateBunker(cx, cz, id, rng, data.heights);
            }
        }

        this._rebuildChunk(id);
        // -- Foliage & Decorator hooks -----------------------------------------
        try { if (window.VoxelFoliage) window.VoxelFoliage.notifyChunkBuilt(cx, cz, data.heights, cs); } catch (_) { }
        try { if (window.VoxelDecorator) window.VoxelDecorator.notifyChunkBuilt(cx, cz, data.heights, cs); } catch (_) { }
    }

    /** Inject a brutalist sci-fi fortress into mutations */
    _generateFortress(cx, cz, id, rng) {
        const cs = this.chunkSize;
        const mods = this._mutations.getChunk(id);
        const centerX = rng.int(cs - 8) + 4;
        const centerZ = rng.int(cs - 8) + 4;
        const size    = rng.int(3) + 3;
        const height  = rng.int(5) + 5;
        const baseH   = this._getHeightAt(cx * cs + centerX, cz * cs + centerZ);
        for (let y = 0; y < height; y++) {
            for (let x = -size; x <= size; x++) {
                for (let z = -size; z <= size; z++) {
                    const isWall = Math.abs(x) === size || Math.abs(z) === size;
                    if (isWall || y === 0 || y === height - 1) {
                        mods[_voxelKey(centerX + x, baseH + y, centerZ + z)] = 3;
                    }
                }
            }
        }
    }

    /** Improved fortress: entrances, simple ramp and corner towers */
    _generateFortressImproved(cx, cz, id, rng) {
        const cs = this.chunkSize;
        const mods   = this._mutations.getChunk(id);
        const centerX = rng.int(cs - 10) + 5;
        const centerZ = rng.int(cs - 10) + 5;
        const size    = rng.int(3) + 3;
        const height  = rng.int(4) + 6;
        const baseH   = this._getHeightAt(cx * cs + centerX, cz * cs + centerZ);

        for (let y = 0; y < height; y++) {
            for (let x = -size; x <= size; x++) {
                for (let z = -size; z <= size; z++) {
                    const isWall = Math.abs(x) === size || Math.abs(z) === size;
                    if (isWall || y === 0 || y === height - 1) {
                        mods[_voxelKey(centerX + x, baseH + y, centerZ + z)] = 3;
                    }
                }
            }
        }

        const doors = [[centerX, centerZ - size], [centerX, centerZ + size], [centerX - size, centerZ], [centerX + size, centerZ]];
        for (const [dx, dz] of doors) {
            for (let dy = 1; dy <= 2; dy++) mods[_voxelKey(dx, baseH + dy, dz)] = 0;
        }

        for (let step = 0; step < Math.min(size + 2, height - 1); step++) {
            const sx = centerX - size + 1 + step;
            const sz = centerZ - size;
            mods[_voxelKey(sx, baseH + step, sz + 1)] = 3;
        }

        const towerOffsets = [[-size, -size], [-size, size], [size, -size], [size, size]];
        for (const [ox, oz] of towerOffsets) {
            const tx = centerX + ox, tz = centerZ + oz;
            const tw = 2 + rng.int(1);
            const th = height + 2;
            for (let y = 0; y < th; y++) {
                for (let x = -tw; x <= tw; x++) {
                    for (let z = -tw; z <= tw; z++) {
                        const isOuter = Math.abs(x) === tw || Math.abs(z) === tw || y === th - 1;
                        if (isOuter) mods[_voxelKey(tx + x, baseH + y, tz + z)] = 3;
                    }
                }
            }
            for (let wy = 1; wy <= Math.min(3, th - 2); wy++) {
                mods[_voxelKey(tx + tw, baseH + wy, tz)] = 0;
                mods[_voxelKey(tx - tw, baseH + wy, tz)] = 0;
            }
        }
    }

    /** Hollow out a subterranean resistance bunker */
    _generateBunker(cx, cz, id, rng, heights) {
        const cs = this.chunkSize;
        const mods   = this._mutations.getChunk(id);
        const centerX = 8, centerZ = 8;
        const surfY   = heights[centerX * cs + centerZ];
        const bunkerY = surfY - 6;
        for (let x = 4; x <= 12; x++) {
            for (let z = 4; z <= 12; z++) {
                for (let y = -2; y <= 1; y++) {
                    mods[_voxelKey(x, bunkerY + y, z)] = 0;
                }
            }
        }
    }

    _countActiveVoxels(data, id) {
        const cs = this.chunkSize;
        const mods = this._mutations.peekChunk(id) || {};
        let solidCols = 0;
        for (let lx = 0; lx < cs; lx++)
            for (let lz = 0; lz < cs; lz++)
                if (mods[_voxelKey(lx, data.heights[lx * cs + lz], lz)] !== 0) solidCols++;
        return solidCols + Object.keys(mods).length;
    }

    // -- _rebuildChunk  (Hybrid Surface Nets + Adaptive Buffers) --------------
    //
    //  Pillar 3 � voxelTypeMask: 1-bit per density node; 1 = structural (player-
    //  placed). When building the vertex for an active cell, if ANY corner that
    //  contributes to a crossing edge is structural, the vertex is pinned to the
    //  integer cell centre instead of the averaged midpoint. This preserves hard
    //  90� edges on built blocks while natural terrain stays smooth.
    //
    //  Pillar 4 � adaptive buffers: chunk grid size is computed from actual
    //  terrain extent � biome amplitude, then buffers are resized on demand.
    // -------------------------------------------------------------------------

    _rebuildChunk(id) {
        const chunk = this._chunks.get(id);
        if (!chunk) return;

        let data = this._dataCache.get(id);
        if (!data) return;

        const [cx, cz] = _parseChunkId(id);
        const cs   = this.chunkSize;
        const mods = this._mutations.peekChunk(id) || {};

        if (!data._density) {
            data = this._computeChunkData(cx, cz);
            this._dataCache.set(id, data);
        }

        const { _density: density, _colorGrid: colorGrid,
                _gridSizeX: GSX, _gridSizeY: GSY, _gridSizeZ: GSZ,
                _minGridY: minGridY } = data;

        const totalNodes  = GSX * GSY * GSZ;
        const neededVerts = totalNodes;                    // upper bound: 1 vert per node
        const neededIdx   = Math.min(neededVerts * 18, neededVerts * 18 + 1024);

        // Expand buffers if this chunk is larger than seen before (Pillar 4)
        _ensureSNBuffers(totalNodes, neededVerts, neededIdx);

        // -- Step 1: bake base density + structural mask into scratch ----------
        _snBufs.density.set(density.subarray(0, totalNodes));
        _snBufs.colorGrid.set(colorGrid.subarray(0, totalNodes * 4));
        // Base terrain has no structural flag
        _snBufs.structuralMask.fill(0, 0, totalNodes);

        const strideYZ = GSY * GSZ;

        // Apply this chunk's mutations
        for (const key in mods) {
            const [lx, ly, lz] = _parseVoxelKey(key);
            const gx = lx + 1, gz = lz + 1;
            const gy = ly - minGridY;
            if (gx < 0 || gx >= GSX || gy < 0 || gy >= GSY || gz < 0 || gz >= GSZ) continue;
            const ni = gx * strideYZ + gy * GSZ + gz;
            const v = mods[key];
            if (v === 0) {
                _snBufs.density[ni] = 0.0;
                _snBufs.structuralMask[ni] = 0;
            } else {
                const matId = (typeof v === 'object' && v) ? v.id : v;
                if (typeof matId === 'number' && matId < 0) continue; // invisible collision-only voxel
                _snBufs.density[ni] = 1.0;
                // Player-placed ? flag as structural for hard-edge vertex pinning
                _snBufs.structuralMask[ni] = 1;
                const col   = this._buildMats.get(matId) || _WHITE;
                const ci4   = ni * 4;
                _snBufs.colorGrid[ci4]     = col[0];
                _snBufs.colorGrid[ci4 + 1] = col[1];
                _snBufs.colorGrid[ci4 + 2] = col[2];
                _snBufs.colorGrid[ci4 + 3] = 1.0;
            }
        }

        // Apply neighbor mutations into padding for seamless chunk borders
        for (let dx = -1; dx <= 1; dx++) {
            for (let dz = -1; dz <= 1; dz++) {
                if (dx === 0 && dz === 0) continue;
                const nmods = this._mutations.peekChunk(`${cx + dx},${cz + dz}`);
                if (!nmods) continue;
                for (const key in nmods) {
                    const [nlx, nly, nlz] = _parseVoxelKey(key);
                    const gx = nlx + 1 + (dx * cs);
                    const gz = nlz + 1 + (dz * cs);
                    const gy = nly - minGridY;
                    if (gx < 0 || gx >= GSX || gy < 0 || gy >= GSY || gz < 0 || gz >= GSZ) continue;
                    const ni = gx * strideYZ + gy * GSZ + gz;
                    const v  = nmods[key];
                    if (v === 0) {
                        _snBufs.density[ni] = 0.0;
                        _snBufs.structuralMask[ni] = 0;
                    } else {
                        const matId = (typeof v === 'object' && v) ? v.id : v;
                        if (typeof matId === 'number' && matId < 0) continue; // invisible collision-only voxel
                        _snBufs.density[ni] = 1.0;
                        _snBufs.structuralMask[ni] = 1;
                        const col   = this._buildMats.get(matId) || _WHITE;
                        const ci4   = ni * 4;
                        _snBufs.colorGrid[ci4]     = col[0];
                        _snBufs.colorGrid[ci4 + 1] = col[1];
                        _snBufs.colorGrid[ci4 + 2] = col[2];
                        _snBufs.colorGrid[ci4 + 3] = 1.0;
                    }
                }
            }
        }

        // -- Step 2: compute one vertex per active cell ------------------------
        _snBufs.vertIdx.fill(-1, 0, totalNodes);
        let vCount = 0, iCount = 0;
        let overflow = false;

        outerVert: for (let gx = 0; gx < GSX - 1; gx++) {
            for (let gy = 0; gy < GSY - 1; gy++) {
                for (let gz = 0; gz < GSZ - 1; gz++) {
                    let solidMask = 0;
                    let hasStructural = false;

                    for (let c = 0; c < 8; c++) {
                        const ni = (gx + _SN_CX[c]) * strideYZ + (gy + _SN_CY[c]) * GSZ + (gz + _SN_CZ[c]);
                        if (_snBufs.density[ni] > 0) {
                            solidMask |= (1 << c);
                            if (_snBufs.structuralMask[ni]) hasStructural = true;
                        }
                    }

                    if (solidMask === 0 || solidMask === 0xFF) continue;

                    let vx = 0, vy = 0, vz = 0, r = 0, g = 0, b = 0, crossCount = 0;

                    for (let e = 0; e < 12; e++) {
                        const ca = _SN_EDGES12_A[e], cb = _SN_EDGES12_B[e];
                        const dA = (solidMask >> ca) & 1, dB = (solidMask >> cb) & 1;
                        if (dA === dB) continue;

                        vx += gx + (_SN_CX[ca] + _SN_CX[cb]) * 0.5;
                        vy += gy + (_SN_CY[ca] + _SN_CY[cb]) * 0.5;
                        vz += gz + (_SN_CZ[ca] + _SN_CZ[cb]) * 0.5;
                        crossCount++;

                        const sc = dA ? ca : cb;
                        const ni2 = (gx + _SN_CX[sc]) * strideYZ + (gy + _SN_CY[sc]) * GSZ + (gz + _SN_CZ[sc]);
                        const c4  = ni2 * 4;
                        r += _snBufs.colorGrid[c4]; g += _snBufs.colorGrid[c4 + 1]; b += _snBufs.colorGrid[c4 + 2];
                    }

                    if (crossCount === 0) continue;
                    if (vCount >= _snBufs.maxVerts) { overflow = true; break outerVert; }

                    const inv = 1.0 / crossCount;
                    const vi3 = vCount * 3, vi4 = vCount * 4;

                    if (hasStructural) {
                        // Pillar 3: pin vertex to integer cell centre for hard-edged built blocks
                        _snBufs.posBuf[vi3]     = gx + 0.5 - 1.0;
                        _snBufs.posBuf[vi3 + 1] = minGridY + gy + 0.5;
                        _snBufs.posBuf[vi3 + 2] = gz + 0.5 - 1.0;
                    } else {
                        // Natural terrain: averaged midpoint for smooth Surface Nets
                        _snBufs.posBuf[vi3]     = vx * inv - 1.0;
                        _snBufs.posBuf[vi3 + 1] = minGridY + vy * inv;
                        _snBufs.posBuf[vi3 + 2] = vz * inv - 1.0;
                    }

                    _snBufs.colBuf[vi4]     = r * inv;
                    _snBufs.colBuf[vi4 + 1] = g * inv;
                    _snBufs.colBuf[vi4 + 2] = b * inv;
                    _snBufs.colBuf[vi4 + 3] = 1.0;

                    _snBufs.vertIdx[gx * strideYZ + gy * GSZ + gz] = vCount;
                    vCount++;
                }
            }
        }

        // -- Step 3: emit quads for each crossing edge -------------------------
        const quadTables = [_SN_QUAD_X, _SN_QUAD_Y, _SN_QUAD_Z];

        outerQuad: for (let gx = 1; gx < GSX - 1; gx++) {
            for (let gy = 1; gy < GSY - 1; gy++) {
                for (let gz = 1; gz < GSZ - 1; gz++) {
                    for (let a = 0; a < 3; a++) {
                        const farC = _SN_EDGE_CB[a];
                        const fgx = gx + _SN_CX[farC], fgy = gy + _SN_CY[farC], fgz = gz + _SN_CZ[farC];
                        if (fgx >= GSX || fgy >= GSY || fgz >= GSZ) continue;

                        const nA = gx * strideYZ + gy * GSZ + gz;
                        const nB = fgx * strideYZ + fgy * GSZ + fgz;
                        const dA = _snBufs.density[nA] > 0 ? 1 : 0, dB = _snBufs.density[nB] > 0 ? 1 : 0;
                        if (dA === dB) continue;

                        if (iCount + 6 > _snBufs.maxIndices) break outerQuad;

                        const qt = quadTables[a];
                        let v0, v1, v2, v3;

                        { const qx = gx + qt[0], qy = gy + qt[4], qz = gz + qt[8]; v0 = (qx >= 0 && qx < GSX - 1 && qy >= 0 && qy < GSY - 1 && qz >= 0 && qz < GSZ - 1) ? _snBufs.vertIdx[qx * strideYZ + qy * GSZ + qz] : -1; }
                        { const qx = gx + qt[1], qy = gy + qt[5], qz = gz + qt[9]; v1 = (qx >= 0 && qx < GSX - 1 && qy >= 0 && qy < GSY - 1 && qz >= 0 && qz < GSZ - 1) ? _snBufs.vertIdx[qx * strideYZ + qy * GSZ + qz] : -1; }
                        { const qx = gx + qt[2], qy = gy + qt[6], qz = gz + qt[10]; v2 = (qx >= 0 && qx < GSX - 1 && qy >= 0 && qy < GSY - 1 && qz >= 0 && qz < GSZ - 1) ? _snBufs.vertIdx[qx * strideYZ + qy * GSZ + qz] : -1; }
                        { const qx = gx + qt[3], qy = gy + qt[7], qz = gz + qt[11]; v3 = (qx >= 0 && qx < GSX - 1 && qy >= 0 && qy < GSY - 1 && qz >= 0 && qz < GSZ - 1) ? _snBufs.vertIdx[qx * strideYZ + qy * GSZ + qz] : -1; }

                        if (v0 < 0 || v1 < 0 || v2 < 0 || v3 < 0) continue;

                        let flip = (dA === 1);
                        if (a === 0 || a === 2) flip = !flip;

                        if (flip) {
                            _snBufs.idxBuf[iCount] = v0; _snBufs.idxBuf[iCount + 1] = v3; _snBufs.idxBuf[iCount + 2] = v1;
                            _snBufs.idxBuf[iCount + 3] = v0; _snBufs.idxBuf[iCount + 4] = v2; _snBufs.idxBuf[iCount + 5] = v3;
                        } else {
                            _snBufs.idxBuf[iCount] = v0; _snBufs.idxBuf[iCount + 1] = v1; _snBufs.idxBuf[iCount + 2] = v3;
                            _snBufs.idxBuf[iCount + 3] = v0; _snBufs.idxBuf[iCount + 4] = v3; _snBufs.idxBuf[iCount + 5] = v2;
                        }
                        iCount += 6;
                    }
                }
            }
        }

        // -- Step 4: unshare vertices for true flat shading --------------------
        for (let ti = 0; ti < iCount; ti += 3) {
            const i0 = _snBufs.idxBuf[ti], i1 = _snBufs.idxBuf[ti + 1], i2 = _snBufs.idxBuf[ti + 2];

            const p0x = _snBufs.posBuf[i0 * 3], p0y = _snBufs.posBuf[i0 * 3 + 1], p0z = _snBufs.posBuf[i0 * 3 + 2];
            const p1x = _snBufs.posBuf[i1 * 3], p1y = _snBufs.posBuf[i1 * 3 + 1], p1z = _snBufs.posBuf[i1 * 3 + 2];
            const p2x = _snBufs.posBuf[i2 * 3], p2y = _snBufs.posBuf[i2 * 3 + 1], p2z = _snBufs.posBuf[i2 * 3 + 2];

            const ex = p1x - p0x, ey = p1y - p0y, ez = p1z - p0z;
            const fx = p2x - p0x, fy = p2y - p0y, fz = p2z - p0z;
            let nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
            const nl = Math.sqrt(nx * nx + ny * ny + nz * nz);

            if (nl > 1e-7) { const inv = 1.0 / nl; nx *= inv; ny *= inv; nz *= inv; }
            else { nx = 0; ny = 1; nz = 0; }

            for (let v = 0; v < 3; v++) {
                const idx    = _snBufs.idxBuf[ti + v];
                const outIdx = ti + v;
                _snBufs.flatPos[outIdx * 3]     = _snBufs.posBuf[idx * 3];
                _snBufs.flatPos[outIdx * 3 + 1] = _snBufs.posBuf[idx * 3 + 1];
                _snBufs.flatPos[outIdx * 3 + 2] = _snBufs.posBuf[idx * 3 + 2];
                _snBufs.flatNrm[outIdx * 3]     = nx;
                _snBufs.flatNrm[outIdx * 3 + 1] = ny;
                _snBufs.flatNrm[outIdx * 3 + 2] = nz;
                _snBufs.flatCol[outIdx * 4]     = _snBufs.colBuf[idx * 4];
                _snBufs.flatCol[outIdx * 4 + 1] = _snBufs.colBuf[idx * 4 + 1];
                _snBufs.flatCol[outIdx * 4 + 2] = _snBufs.colBuf[idx * 4 + 2];
                _snBufs.flatCol[outIdx * 4 + 3] = _snBufs.colBuf[idx * 4 + 3];
                _snBufs.flatIdx[outIdx] = outIdx;
            }
        }

        if (vCount === 0 || iCount === 0) {
            const emptyMesh = new Mesh(this.engine, {
                vertexCount: 3,
                indexData: new Uint32Array([0, 1, 2]),
                indexType: MeshIndexType.UnsignedInt,
            });
            emptyMesh.update();
            if (chunk.mesh) chunk.mesh.destroy();
            const mc = chunk.root.getComponent('mesh');
            if (mc) mc.mesh = emptyMesh;
            chunk.mesh = emptyMesh;
            return;
        }

        // -- Step 5: upload into Wonderland Engine Mesh ------------------------
        const mesh = new Mesh(this.engine, {
            vertexCount: iCount,
            indexData: new Uint32Array(_snBufs.flatIdx.buffer, 0, iCount),
            indexType: MeshIndexType.UnsignedInt,
        });

        const positions = mesh.attribute(MeshAttribute.Position);
        const normals   = mesh.attribute(MeshAttribute.Normal);
        const colors    = mesh.attribute(MeshAttribute.Color);

        for (let vi = 0; vi < iCount; vi++) {
            _snPos0[0] = _snBufs.flatPos[vi * 3]; _snPos0[1] = _snBufs.flatPos[vi * 3 + 1]; _snPos0[2] = _snBufs.flatPos[vi * 3 + 2];
            _snPos1[0] = _snBufs.flatNrm[vi * 3]; _snPos1[1] = _snBufs.flatNrm[vi * 3 + 1]; _snPos1[2] = _snBufs.flatNrm[vi * 3 + 2];
            _snColScratch[0] = _snBufs.flatCol[vi * 4]; _snColScratch[1] = _snBufs.flatCol[vi * 4 + 1];
            _snColScratch[2] = _snBufs.flatCol[vi * 4 + 2]; _snColScratch[3] = _snBufs.flatCol[vi * 4 + 3];
            positions.set(vi, _snPos0);
            normals.set(vi, _snPos1);
            colors.set(vi, _snColScratch);
        }

        mesh.update();
        const mc = chunk.root.getComponent('mesh');
        if (mc) mc.mesh = mesh;
        if (chunk.mesh) chunk.mesh.destroy();
        chunk.mesh = mesh;
    }

    _destroyChunk(id, chunk) {
        // Inform foliage & decorator systems
        try { 
            const [cx, cz] = _parseChunkId(id); 
            if (window.VoxelFoliage) window.VoxelFoliage.notifyChunkDestroyed(cx, cz); 
            if (window.VoxelDecorator) window.VoxelDecorator.notifyChunkDestroyed(cx, cz);
        } catch (_) { }
        if (chunk.mesh) chunk.mesh.destroy();
        chunk.root.destroy();
        this._chunks.delete(id);
    }

    // -- Terrain data  (Pillar 4: adaptive grid sizing) ------------------------

    _computeChunkData(cx, cz) {
        const cs    = this.chunkSize;
        const depth = this.columnDepth;

        // -- Canonical (cs�cs) surface data -----------------------------------
        const heights   = new Int16Array(cs * cs);
        const colorsFlat = new Float32Array(cs * cs * 4);
        const subFlat   = new Float32Array(cs * cs * 4 * 4);

        for (let lx = 0; lx < cs; lx++) {
            for (let lz = 0; lz < cs; lz++) {
                const wx = cx * cs + lx, wz = cz * cs + lz;
                const { height, colorFlat } = this._sampler.sample(wx, wz);
                const idx = lx * cs + lz;
                heights[idx] = height;
                const ci = idx * 4;
                colorsFlat[ci] = colorFlat[0]; colorsFlat[ci + 1] = colorFlat[1];
                colorsFlat[ci + 2] = colorFlat[2]; colorsFlat[ci + 3] = 1.0;
                for (let dy = 1; dy <= 4; dy++) {
                    const sc = this._sampler.subColor(wx, wz, dy);
                    const si = (idx * 4 + (dy - 1)) * 4;
                    subFlat[si] = sc[0]; subFlat[si + 1] = sc[1];
                    subFlat[si + 2] = sc[2]; subFlat[si + 3] = 1.0;
                }
            }
        }
        const colorViews = _makeFlatColorAccessor(colorsFlat, cs);
        const subViews = new Array(cs * cs);
        for (let i = 0; i < cs * cs; i++) {
            subViews[i] = new Array(4);
            for (let d = 0; d < 4; d++) subViews[i][d] = subFlat.subarray((i * 4 + d) * 4, (i * 4 + d) * 4 + 4);
        }

        // -- Adaptive vertical extent (Pillar 4) -------------------------------
        // Compute tight min/max from the sampled height array, then scale by the
        // dominant biome's heightAmp to give the grid the correct headroom without
        // wasting memory on flat biomes.
        let minH = heights[0], maxH = heights[0];
        for (let i = 1; i < cs * cs; i++) {
            if (heights[i] < minH) minH = heights[i];
            if (heights[i] > maxH) maxH = heights[i];
        }
        // Sample dominant biome amplitude at chunk centre for headroom scaling
        const midWx = cx * cs + (cs >> 1), midWz = cz * cs + (cs >> 1);
        const dominantBiome = this._sampler.dominantBiomeAt(midWx, midWz);
        const biomeAmp = dominantBiome ? (dominantBiome.heightAmp || 1.0) : 1.0;
        // Extra headroom = mutation height budget (12 blocks) � biome amplitude,
        // plus 4-block safety margin to prevent structural block overflows.
        const mutationHeadroom = Math.ceil(12 * biomeAmp) + 4;

        const minGridY   = Math.min(minH - depth, Math.floor(this.floorLevel)) - 2;
        const gridSizeX  = cs + 2;
        const gridSizeY  = (Math.max(maxH + mutationHeadroom, Math.floor(this.floorLevel)) - minGridY) + 3;
        const gridSizeZ  = cs + 2;
        const totalNodes = gridSizeX * gridSizeY * gridSizeZ;

        // Allocate per-chunk density/color arrays (these are owned by ChunkDataCache)
        const density   = new Float32Array(totalNodes);
        const colorGrid = new Float32Array(totalNodes * 4);

        for (let gx = 0; gx < gridSizeX; gx++) {
            for (let gz = 0; gz < gridSizeZ; gz++) {
                const wx = cx * cs + gx - 1;
                const wz = cz * cs + gz - 1;

                const lxClamp = Math.max(0, Math.min(cs - 1, gx - 1));
                const lzClamp = Math.max(0, Math.min(cs - 1, gz - 1));
                let surfH, r, g, b;
                if (gx >= 1 && gx <= cs && gz >= 1 && gz <= cs) {
                    const idx = lxClamp * cs + lzClamp;
                    surfH = heights[idx];
                    const ci = idx * 4;
                    r = colorsFlat[ci]; g = colorsFlat[ci + 1]; b = colorsFlat[ci + 2];
                } else {
                    const { height, colorFlat } = this._sampler.sample(wx, wz);
                    surfH = height;
                    r = colorFlat[0]; g = colorFlat[1]; b = colorFlat[2];
                }

                const strideYZ = gridSizeY * gridSizeZ;
                // Clamp surfH to floorLevel so deep-ocean/void areas still get a
                // solid floor tile instead of leaving an empty hole in the mesh.
                const effectiveSurfH = Math.max(surfH, Math.floor(this.floorLevel));
                for (let gy = 0; gy < gridSizeY; gy++) {
                    const wy = minGridY + gy;
                    const ni = gx * strideYZ + gy * gridSizeZ + gz;
                    const solid = wy <= effectiveSurfH && wy >= this.floorLevel;
                    density[ni] = solid ? 1.0 : 0.0;
                    const ci4 = ni * 4;
                    if (solid) {
                        const depthLayer = effectiveSurfH - wy;
                        if (depthLayer === 0) {
                            colorGrid[ci4] = r; colorGrid[ci4 + 1] = g; colorGrid[ci4 + 2] = b;
                        } else {
                            const sc = this._sampler.subColor(wx, wz, depthLayer);
                            colorGrid[ci4] = sc[0]; colorGrid[ci4 + 1] = sc[1]; colorGrid[ci4 + 2] = sc[2];
                        }
                        colorGrid[ci4 + 3] = 1.0;
                    }
                }
            }
        }

        return {
            heights, _colorsFlat: colorsFlat, colors: colorViews, subColors: subViews,
            _density: density, _colorGrid: colorGrid,
            _gridSizeX: gridSizeX, _gridSizeY: gridSizeY, _gridSizeZ: gridSizeZ,
            _minGridY: minGridY,
        };
    }

    _getHeightAt(wx, wz) { return this._sampler.sampleHeight(wx, wz); }

    /**
     * Bilinear-interpolated surface height at a continuous world (wx, wz).
     * Delegates to the module-level getSurfaceHeight() so foliage and collision
     * use the same smooth surface as the mesh.
     */
    getSurfaceHeight(wx, wz) { return getSurfaceHeight(this._sampler, wx, wz); }

    /**
     * Returns a safe Y to place the player above any terrain in a (2*radius+1)²
     * neighbourhood around (wx, wz).  Adds 3 voxels of headroom above the
     * highest neighbour so the player never clips into a steep hillside.
     */
    getSafeSpawnY(wx, wz, radius = 2) {
        const ox = Math.floor(wx), oz = Math.floor(wz);
        let maxH = -Infinity;
        for (let dx = -radius; dx <= radius; dx++) {
            for (let dz = -radius; dz <= radius; dz++) {
                const h = this._sampler.sampleHeight(ox + dx, oz + dz);
                if (h > maxH) maxH = h;
            }
        }
        // Scan upward from maxH to clear any overhead solid voxels in already-loaded chunks
        // (e.g. decorator structures that noise-based sampleHeight doesn't see).
        let topY = maxH;
        for (let y = maxH + 1; y <= maxH + 24; y++) {
            if (this._isSolid(ox, y, oz)) topY = y;
        }
        return topY + 3;
    }

    /**
     * Reset the player to a safe surface position with full health/shield.
     * Called by GameInterface.respawnPlayer() after the death screen countdown.
     */
    respawnPlayer() {
        this._health  = 100;
        this._shield  = 100;
        this._isDead  = false;
        this._velY    = 0;
        this._velX    = 0;
        this._velZ    = 0;

        // Immediately clear any in-voxel occlusion that was active at death.
        this._wasInsideVoxel = false;
        if (this.voxelOcclusionObject)   this.voxelOcclusionObject.active   = false;
        if (this.voxelOcclusionObjectVR) this.voxelOcclusionObjectVR.active = false;

        // Find a clear surface near world origin to respawn
        try {
            const spawnX = (Math.random() - 0.5) * 8;
            const spawnZ = (Math.random() - 0.5) * 8;
            const safeY  = this.getSafeSpawnY(spawnX, spawnZ);
            this.object.getPositionWorld(_wpBuf);
            _wpBuf[0] = -spawnX;
            _wpBuf[1] = -safeY;
            _wpBuf[2] = -spawnZ;
            this.object.setPositionWorld(_wpBuf);
            this._playerCX = null; this._playerCZ = null; // force chunk refresh
            const pcx = Math.floor(spawnX / this.chunkSize);
            const pcz = Math.floor(spawnZ / this.chunkSize);
            this._updateWorld(pcx, pcz);
        } catch (_) {}

        if (window.GameInterface) window.GameInterface.updateStats(this._health, this._shield);
    }

    /**
     * Respawn after a PvP kill — keeps the run alive, teleports to a random
     * surface point within 30 units of where the player died.
     */
    pvpRespawn() {
        this._health = 100;
        this._shield = 100;
        this._isDead = false;
        this._velY   = 0;
        this._velX   = 0;
        this._velZ   = 0;
        this._wasInsideVoxel = false;
        if (this.voxelOcclusionObject)   this.voxelOcclusionObject.active   = false;
        if (this.voxelOcclusionObjectVR) this.voxelOcclusionObjectVR.active = false;

        try {
            this.object.getPositionWorld(_wpBuf);
            const curWX = -_wpBuf[0];
            const curWZ = -_wpBuf[2];

            // Pick a random surface point 10–30 units away from death location
            const angle  = Math.random() * Math.PI * 2;
            const dist   = 10 + Math.random() * 20;
            const spawnX = curWX + Math.cos(angle) * dist;
            const spawnZ = curWZ + Math.sin(angle) * dist;
            const safeY  = this.getSafeSpawnY(spawnX, spawnZ);

            _wpBuf[0] = -spawnX;
            _wpBuf[1] = -safeY;
            _wpBuf[2] = -spawnZ;
            this.object.setPositionWorld(_wpBuf);
            this._playerCX = null;
            this._playerCZ = null;
            this._updateWorld(Math.floor(spawnX / this.chunkSize), Math.floor(spawnZ / this.chunkSize));
        } catch (_) {}

        if (window.GameInterface) window.GameInterface.updateStats(this._health, this._shield);
    }

    /**
     * Apply a voxel edit received from a remote peer.
     * value === 0  → block was mined (air); value > 0 → block placed (material id).
     * Skips the _onVoxelEdit callback so we don't re-broadcast the remote edit.
     */
    applyRemoteEdit(wx, wy, wz, value, ts) {
        try {
            const cs = this.chunkSize;
            const fx = Math.floor(wx), fy = Math.floor(wy), fz = Math.floor(wz);
            const cx = Math.floor(fx / cs), cz = Math.floor(fz / cs);
            const chunkId = `${cx},${cz}`;
            const localX = ((fx % cs) + cs) % cs;
            const localZ = ((fz % cs) + cs) % cs;
            const key = `${localX}_${fy}_${localZ}`;
            this._mutations.set(chunkId, key, value);
            this._queueChunkWrite(chunkId);
            if (this._chunks.has(chunkId)) this._rebuildChunk(chunkId);
            if (value === 0) {
                for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
                    const nid = `${cx + dx},${cz + dz}`;
                    if (this._chunks.has(nid)) this._rebuildChunk(nid);
                }
                // Notify TreeHandler so the 3D mesh and remaining trunk voxels are removed.
                try { window.TreeHandler?.notifyVoxelRemoved(fx, fy, fz); } catch (_) {}
            }
        } catch (_) {}
    }

    // -- Unified block lookup  (Pillar 1: single sampling pass) ---------------
    //
    //  Query order:
    //    1. VoxelModificationMap  (player mutations � highest priority)
    //    2. Base WorldSampler height field (terrain)
    //    3. VoxelFoliage hook (so foliage stays collideable)
    //
    //  Both mesh generation AND physics/collision call this path, eliminating
    //  the separate sampleHeight() path that caused visual/collision drift.
    // -------------------------------------------------------------------------

    _getBlockAt(wx, wy, wz) {
        const cs = this.chunkSize;
        const cx = Math.floor(wx / cs), cz = Math.floor(wz / cs);
        const id = `${cx},${cz}`;
        const lx = ((wx % cs) + cs) % cs, lz = ((wz % cs) + cs) % cs;
        const key = _voxelKey(lx, wy, lz);

        // 1. Mutation query (Pillar 1: VoxelModificationMap first)
        const mutVal = this._mutations.get(id, key);
        if (mutVal !== undefined) {
            return mutVal === 0 ? null : { chunkId: id, lx, ly: wy, lz, built: true };
        }

        // 2. Base terrain via unified sampler
        let data = this._dataCache.get(id);
        if (!data) { data = this._computeChunkData(cx, cz); this._dataCache.set(id, data); }
        const surfY = data.heights[lx * cs + lz];
        if (wy <= surfY && wy >= this.floorLevel) return { chunkId: id, lx, ly: wy, lz, built: false };

        // 3. Foliage hook (intact � voxel-foliage.js compatibility)
        try {
            if (typeof window !== 'undefined' && window.VoxelFoliage && typeof window.VoxelFoliage.isFoliageAt === 'function') {
                if (window.VoxelFoliage.isFoliageAt(wx, wy, wz)) {
                    return { chunkId: id, lx, ly: wy, lz, built: false };
                }
            }
            if (typeof window !== 'undefined' && window.VoxelDecorator && typeof window.VoxelDecorator.isFoliageAt === 'function') {
                if (window.VoxelDecorator.isFoliageAt(wx, wy, wz)) {
                    return { chunkId: id, lx, ly: wy, lz, built: false };
                }
            }
        } catch (_) { }

        return null;
    }

    _isSolid(wx, wy, wz) { return this._getBlockAt(Math.floor(wx), Math.floor(wy), Math.floor(wz)) !== null; }

    // -- Voxel damage / mining -------------------------------------------------

    _initialHPForBlock(id) {
        const map = { 1: 30, 2: 55, 3: 100, 4: 160, 5: 280 };
        return (id && this._buildMats.has(id)) ? (map[id] || 50) : 60;
    }

    _applyDamageToVoxel(lx, ly, lz, damage, _shooterId) {
        if (ly <= this.floorLevel) return false;

        const cs = this.chunkSize;
        const cx = Math.floor(lx / cs), cz = Math.floor(lz / cs);
        const chunkId = `${cx},${cz}`;
        const localLx = ((lx % cs) + cs) % cs, localLz = ((lz % cs) + cs) % cs;
        const key = _voxelKey(localLx, ly, localLz);

        const curVal = this._mutations.get(chunkId, key);
        if (curVal === 0) return false;

        let mods = this._mutations.getChunk(chunkId);
        let cur  = mods[key];
        let id   = 0;

        if (cur == null) {
            const block = this._getBlockAt(lx, ly, lz);
            if (!block) return false;
            id = 1;
            let hp = Math.min(this._initialHPForBlock(id), Math.max(1, damage * 3));
            cur = { id, hp, maxHp: hp };
            mods[key] = cur;
        } else if (typeof cur === 'object') {
            id = cur.id || 0; if (!id) return false;
        } else {
            id = cur;
            let hp = Math.min(this._initialHPForBlock(id), Math.max(1, damage * 3));
            cur = { id, hp, maxHp: hp };
            mods[key] = cur;
        }

        cur.hp -= damage;
        if (cur.hp <= 0) {
            mods[key] = 0;
            try { this._onVoxelEdit?.(lx, ly, lz, 0); } catch (_) {}
            this._enqueueChunk(cx, cz, chunkId);
            this._queueChunkWrite(chunkId);
            try {
                if (this._chunks.has(chunkId)) this._rebuildChunk(chunkId);
                for (const [ndx, ndz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
                    const nid = `${cx + ndx},${cz + ndz}`;
                    if (this._chunks.has(nid)) this._rebuildChunk(nid);
                }
            } catch (_) { }
            this._awardMiningResource(lx, ly, lz, chunkId);
            // Foliage hook (intact)
            try { if (window.VoxelFoliage) window.VoxelFoliage.notifyVoxelRemoved(lx, ly, lz); } catch (_) { }
            try { if (window.VoxelDecorator) window.VoxelDecorator.notifyVoxelRemoved(lx, ly, lz); } catch (_) { }
            try { if (window.TreeHandler) window.TreeHandler.notifyVoxelRemoved(lx, ly, lz); } catch (_) { }
            return true;
        }
        this._queueChunkWrite(chunkId);
        return false;
    }

    _awardMiningResource(wx, wy, wz, chunkId) {
        try {
            const cs   = this.chunkSize;
            const data = this._dataCache.get(chunkId);
            const llx  = ((wx % cs) + cs) % cs, llz = ((wz % cs) + cs) % cs;
            const surfY = data ? data.heights[llx * cs + llz] : wy;
            const isSurface = (wy === surfY);
            const depth = surfY - wy;
            const sc = this._sampler.resourceAt(wx, wz, 0, isSurface, depth);
            this._spawnPickup(sc.type, sc.amount, [wx, wy, wz], sc.desc, 1);
            if (window.GameInterface) window.GameInterface.pushConsoleLog(`MINED: ${sc.desc}`);
        } catch (_) { }
    }

    // -- Build action ----------------------------------------------------------

    async _onBuild(hit = null) {
        if (!this.active) return;
        const MATS = {
            1: { mineral: 'alloy',    cost: 3, name: 'ALLOY PLATING'   },
            2: { mineral: 'biomass',  cost: 3, name: 'BIOMASS PANEL'   },
            3: { mineral: 'titanium', cost: 2, name: 'TITANIUM SLAB'   },
            4: { mineral: 'plasma',   cost: 2, name: 'PLASMA FORGE'    },
            5: { mineral: 'crystal',  cost: 1, name: 'CRYSTAL BASTION' },
        };
        const matDef = MATS[this.buildMaterialId];
        const inv = this._inventory;
        let canBuild = false;
        if (matDef && (inv[matDef.mineral] || 0) >= matDef.cost) {
            inv[matDef.mineral] -= matDef.cost;
            canBuild = true;
        }
        if (!canBuild) { if (window.GameInterface) window.GameInterface.pushConsoleLog('RESOURCE INSUFFICIENT FOR FABRICATION'); return; }

        if (!hit) hit = this._raycastCenter();
        if (!hit) return;
        const { chunkId, lx, ly, lz, normal } = hit;
        let [cx, cz] = _parseChunkId(chunkId);
        let wx = lx + normal.x, wy = ly + normal.y, wz = lz + normal.z;
        const cs = this.chunkSize;
        if (wx < 0) { cx--; wx += cs; } if (wz < 0) { cz--; wz += cs; }
        if (wx >= cs) { cx++; wx -= cs; } if (wz >= cs) { cz++; wz -= cs; }
        const targetId = `${cx},${cz}`;
        if (this._getBlockAt(cx * cs + wx, wy, cz * cs + wz)) return;
        if (!this._chunks.has(targetId)) {
            this._enqueueChunk(cx, cz, targetId);
            try { await this._waitForChunk(targetId); } catch (_) { return; }
        }
        const mods = this._mutations.getChunk(targetId);
        mods[_voxelKey(wx, wy, wz)] = this.buildMaterialId;
        try { this._onVoxelEdit?.(cx * cs + wx, wy, cz * cs + wz, this.buildMaterialId); } catch (_) {}
        this._rebuildChunk(targetId);
        this._queueChunkWrite(targetId);
        if (window.GameInterface) {
            window.GameInterface.updateResources(inv.alloy, inv.biomass, inv.titanium, inv.plasma, inv.crystal);
            window.GameInterface.pushConsoleLog(`DEPLOYED ${matDef ? matDef.name : 'ELEMENT'} [ID: ${this.buildMaterialId}]`);
            window.GameInterface.refreshCraftPanel?.();
        }
    }

    _enqueueChunk(cx, cz, id) {
        if (this._chunks.has(id) || this._genQueueSet.has(id)) return;
        const dx = cx - (this._playerCX || 0), dz = cz - (this._playerCZ || 0);
        this._genQueue.push({ cx, cz, id, pri: dx * dx + dz * dz });
        this._genQueueSet.add(id);
        this._genQueue.sort((a, b) => a.pri - b.pri);
        try { this._drainQueue(); } catch (_) { }
    }

    _waitForChunk(id, timeoutMs = 1000) {
        return new Promise((resolve, reject) => {
            const start = performance.now();
            const check = () => {
                if (this._chunks.has(id)) return resolve();
                if (performance.now() - start >= timeoutMs) return reject(new Error('timeout'));
                setTimeout(check, 16);
            };
            check();
        });
    }

    _applyMutationsToLoadedChunks() {
        for (const [chunkId] of this._mutations.entries()) {
            if (this._chunks.has(chunkId)) this._rebuildChunk(chunkId);
        }
    }

    /** Remove all fortress (material id 3) mutations from persisted and live chunks */
    clearFortressVoxels() {
        let cleared = 0;
        for (const [chunkId, mods] of this._mutations.entries()) {
            let changed = false;
            for (const key of Object.keys(mods)) {
                const v = mods[key];
                if (v === 3 || (typeof v === 'object' && v && v.id === 3)) {
                    delete mods[key]; changed = true;
                }
            }
            if (changed) {
                this._queueChunkWrite(chunkId);
                cleared++;
                try { if (this._chunks.has(chunkId)) this._rebuildChunk(chunkId); } catch (_) { }
                try {
                    const [cx, cz] = _parseChunkId(chunkId);
                    for (const [ndx, ndz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
                        const nid = `${cx + ndx},${cz + ndz}`;
                        if (this._chunks.has(nid)) this._rebuildChunk(nid);
                    }
                } catch (_) { }
            }
        }
        try { console.log(`[VoxelTreadmill] clearFortressVoxels: cleared fortress voxels in ${cleared} chunk(s)`); } catch (_) { }
        return cleared;
    }

    // -- Camera & look ---------------------------------------------------------

    _getForward(out) {
        // In VR, read forward from the assigned vrHead object (HMD/XR camera node).
        // VR stick turns rotate the rig but never call _applyCameraRotation(), so the
        // desktop camera's world forward is stale — vrHead tracks the actual head pose.
        if (!!(this.engine.xr?.session) && this.vrHead) {
            this.vrHead.getForwardWorld(out);
            out[1] = 0;
            vec3.normalize(out, out);
            return out;
        }
        if (this.camera && typeof this.camera.getForwardWorld === 'function') {
            this.camera.getForwardWorld(out); vec3.normalize(out, out); return out;
        }
        out[0] = 0; out[1] = 0; out[2] = -1; return out;
    }

    _applyCameraRotation() {
        if (!this.camera) return;
        quat.identity(_qYaw); quat.rotateY(_qYaw, _qYaw, this._lookYaw);
        quat.identity(_qPitch); quat.rotateX(_qPitch, _qPitch, this._lookPitch);
        quat.multiply(_qFinal, _qYaw, _qPitch);
        this.camera.setRotationLocal(_qFinal);
    }

    /**
     * DDA raycast from an explicit world-space origin + direction.
     * Mirrors _raycastCenter() but takes origin/dir instead of reading from camera.
     * origin: [x,y,z] world position; dir: [x,y,z] direction (normalised internally).
     */
    _raycastFrom(origin, dir, maxDist = null) {
        if (maxDist === null) maxDist = this.interactionDistance;
        this.object.getPositionWorld(_objWBuf);
        // Normalise direction
        let dx = dir[0], dy = dir[1], dz = dir[2];
        const len = Math.sqrt(dx*dx + dy*dy + dz*dz);
        if (len < 0.0001) return null;
        dx /= len; dy /= len; dz /= len;
        // Convert world origin to VT-local space (VT root is at negative player pos)
        const sx = origin[0] - _objWBuf[0];
        const sy = origin[1] - _objWBuf[1];
        const sz = origin[2] - _objWBuf[2];
        let x = Math.floor(sx), y = Math.floor(sy), z = Math.floor(sz);
        const stX = Math.sign(dx) || 1, stY = Math.sign(dy) || 1, stZ = Math.sign(dz) || 1;
        const adX = Math.abs(dx), adY = Math.abs(dy), adZ = Math.abs(dz);
        let tmX = dx === 0 ? Infinity : (stX > 0 ? (Math.floor(sx)+1-sx)/dx : (sx-Math.floor(sx))/adX);
        let tmY = dy === 0 ? Infinity : (stY > 0 ? (Math.floor(sy)+1-sy)/dy : (sy-Math.floor(sy))/adY);
        let tmZ = dz === 0 ? Infinity : (stZ > 0 ? (Math.floor(sz)+1-sz)/dz : (sz-Math.floor(sz))/adZ);
        const tdX = dx === 0 ? Infinity : Math.abs(1/dx);
        const tdY = dy === 0 ? Infinity : Math.abs(1/dy);
        const tdZ = dz === 0 ? Infinity : Math.abs(1/dz);
        let dist = 0;
        const normal = { x: 0, y: 1, z: 0 };
        let hitBlock = this._getBlockAt(x, y, z);
        if (!hitBlock) {
            while (dist < maxDist) {
                if (tmX < tmY) {
                    if (tmX < tmZ) { dist = tmX; tmX += tdX; x += stX; normal.x = -stX; normal.y = 0; normal.z = 0; }
                    else           { dist = tmZ; tmZ += tdZ; z += stZ; normal.x = 0;    normal.y = 0; normal.z = -stZ; }
                } else {
                    if (tmY < tmZ) { dist = tmY; tmY += tdY; y += stY; normal.x = 0; normal.y = -stY; normal.z = 0; }
                    else           { dist = tmZ; tmZ += tdZ; z += stZ; normal.x = 0; normal.y = 0;    normal.z = -stZ; }
                }
                if (dist > maxDist) break;
                hitBlock = this._getBlockAt(x, y, z);
                if (hitBlock) break;
            }
        }
        if (!hitBlock) return null;
        return { dist, chunkId: hitBlock.chunkId, lx: hitBlock.lx, ly: hitBlock.ly, lz: hitBlock.lz, normal };
    }

    // -- Amanatides-Woo DDA Raycast  (Pillar 2) --------------------------------
    //
    //  Pure grid DDA � no reliance on visual mesh triangle intersections.
    //  The algorithm steps through integer voxel cells along the look-ray and
    //  detects the first solid-to-air (or air-to-solid) transition, returning
    //  the hit voxel, the face normal, and the distance.
    // -------------------------------------------------------------------------

    _raycastCenter() {
        const maxDist = this.interactionDistance;
        this.object.getPositionWorld(_objWBuf);
        const camW = (this.camera && typeof this.camera.getPositionWorld === 'function')
            ? this.camera.getPositionWorld(_camWBuf) : _objWBuf;
        this._getForward(_fwdBuf);
        const dir = _fwdBuf;
        const sx = camW[0] - _objWBuf[0], sy = camW[1] - _objWBuf[1], sz = camW[2] - _objWBuf[2];
        let x = Math.floor(sx), y = Math.floor(sy), z = Math.floor(sz);
        const stX = Math.sign(dir[0]) || 1, stY = Math.sign(dir[1]) || 1, stZ = Math.sign(dir[2]) || 1;
        const adX = Math.abs(dir[0]), adY = Math.abs(dir[1]), adZ = Math.abs(dir[2]);
        let tmX = dir[0] === 0 ? Infinity : (stX > 0 ? (Math.floor(sx) + 1 - sx) / dir[0] : (sx - Math.floor(sx)) / adX);
        let tmY = dir[1] === 0 ? Infinity : (stY > 0 ? (Math.floor(sy) + 1 - sy) / dir[1] : (sy - Math.floor(sy)) / adY);
        let tmZ = dir[2] === 0 ? Infinity : (stZ > 0 ? (Math.floor(sz) + 1 - sz) / dir[2] : (sz - Math.floor(sz)) / adZ);
        const tdX = dir[0] === 0 ? Infinity : Math.abs(1 / dir[0]);
        const tdY = dir[1] === 0 ? Infinity : Math.abs(1 / dir[1]);
        const tdZ = dir[2] === 0 ? Infinity : Math.abs(1 / dir[2]);
        let dist = 0;
        const normal = { x: 0, y: 1, z: 0 };
        let hitBlock = this._getBlockAt(x, y, z);
        if (!hitBlock) {
            while (dist < maxDist) {
                if (tmX < tmY) {
                    if (tmX < tmZ) { dist = tmX; tmX += tdX; x += stX; normal.x = -stX; normal.y = 0; normal.z = 0; }
                    else           { dist = tmZ; tmZ += tdZ; z += stZ; normal.x = 0;    normal.y = 0; normal.z = -stZ; }
                } else {
                    if (tmY < tmZ) { dist = tmY; tmY += tdY; y += stY; normal.x = 0; normal.y = -stY; normal.z = 0; }
                    else           { dist = tmZ; tmZ += tdZ; z += stZ; normal.x = 0; normal.y = 0;    normal.z = -stZ; }
                }
                if (dist > maxDist) break;
                hitBlock = this._getBlockAt(x, y, z);
                if (hitBlock) break;
            }
        }
        if (!hitBlock) return null;
        return { dist, chunkId: hitBlock.chunkId, lx: hitBlock.lx, ly: hitBlock.ly, lz: hitBlock.lz, normal };
    }

    // -- Predictive Target Voxel  (Pillar 2) -----------------------------------
    //
    //  getPredictiveTargetVoxel(hitPosition, hitNormal, intent)
    //
    //  For intent='BUILD': projects hit normal to nearest cardinal axis, returns
    //  the integer-grid cell adjacent to the hit surface. If that cell is already
    //  occupied, auto-pushes outward one more step to find free air.
    //
    //  For intent='MINE': returns the integer cell of the hit block itself.
    //
    //  @param {{x,y,z}} hitPosition  � continuous world position of the hit point
    //  @param {{x,y,z}} hitNormal    � surface normal from DDA raycast
    //  @param {'BUILD'|'MINE'} intent
    //  @returns {{ wx: int, wy: int, wz: int, cardinal: int[] }}
    // -------------------------------------------------------------------------

    getPredictiveTargetVoxel(hitPosition, hitNormal, intent) {
        const cardinal = _snapToCardinal(hitNormal.x || 0, hitNormal.y || 0, hitNormal.z || 0);
        const bx = Math.floor(hitPosition.x), by = Math.floor(hitPosition.y), bz = Math.floor(hitPosition.z);
        if (intent === 'MINE') return { wx: bx, wy: by, wz: bz, cardinal };

        // BUILD: step along cardinal to the adjacent free cell
        let tx = bx + cardinal[0], ty = by + cardinal[1], tz = bz + cardinal[2];
        // If adjacent cell is occupied, push one more step outward (up to 3)
        for (let attempt = 0; attempt < 3; attempt++) {
            if (!this._getBlockAt(tx, ty, tz)) break;
            tx += cardinal[0]; ty += cardinal[1]; tz += cardinal[2];
        }
        return { wx: tx, wy: ty, wz: tz, cardinal };
    }

    // -- Ghost Block / Placement Preview  (Pillar 2) ---------------------------
    //
    //  getPlacementGhostTransform(ray) ? { position: [x,y,z], size: [1,1,1] }
    //
    //  Returns an integer-snapped bounding box centred on the target placement
    //  cell. The transform is stable: it only changes when the DDA ray crosses
    //  a logical cell boundary, not on every sub-voxel mouse movement.
    //
    //  @param {{ origin: {x,y,z}, direction: {x,y,z} }} ray  � optional;
    //         defaults to the current camera look ray
    //  @returns {{ position: [x,y,z], size: [1,1,1] } | null}
    // -------------------------------------------------------------------------

    getPlacementGhostTransform(ray) {
        const hit = this._raycastCenter();
        if (!hit) return null;
        const [cx, cz] = _parseChunkId(hit.chunkId);
        const cs = this.chunkSize;
        const nx = hit.normal.x, ny = hit.normal.y, nz = hit.normal.z;
        // Integer position of the build target (face-adjacent free cell)
        const tx = cx * cs + hit.lx + Math.round(nx);
        const ty = hit.ly           + Math.round(ny);
        const tz = cz * cs + hit.lz + Math.round(nz);
        // Only show ghost if the target cell is actually free
        if (this._getBlockAt(tx, ty, tz)) return null;
        return {
            position: [tx + 0.5, ty + 0.5, tz + 0.5], // world-space centre
            size:     [1, 1, 1],
        };
    }

    // -- Persistence: serialization facade  (Pillar 1) -------------------------

    /**
     * Export all player modifications as a plain JSON-safe object.
     * Safe to pass to JSON.stringify and deserializeModifications().
     */
    serializeModifications() {
        return this._mutations.serializeModifications();
    }

    /**
     * Restore player modifications from a snapshot. Rebuilds all affected
     * chunks that are currently loaded in the scene.
     * @param {object} data � return value of serializeModifications()
     */
    deserializeModifications(data) {
        this._mutations.deserializeModifications(data);
        this._applyMutationsToLoadedChunks();
    }

    // -- Material cache --------------------------------------------------------

    _getMat(rgba) {
        const key = `${rgba[0] * 100 | 0},${rgba[1] * 100 | 0},${rgba[2] * 100 | 0}`;
        if (this._matCache.has(key)) return this._matCache.get(key);
        const mat = this._matMake(rgba); this._matCache.set(key, mat); return mat;
    }

    // -- Persistence (IndexedDB) -----------------------------------------------

    // -- Persistence (IndexedDB � Full) ------------------------------------------
    //
    //  DB schema v3:
    //    mutations   � { chunkId (pk), modifications: {key?value}, ts: number }
    //    playerState � { id: 'player' (pk), inventory, health, shield,
    //                    lookYaw, lookPitch, posX, posY, posZ }
    //    worldMeta   � { id: 'meta' (pk), noiseSeed, savedAt, version }
    //
    //  Writes are batched via a 120 ms debounce timer.
    //  Player state is auto-saved every 5 s and on page unload.
    // -------------------------------------------------------------------------

    _initDB() {
        return new Promise((resolve) => {
            try {
                const req = window.indexedDB.open('VoxelWorldDB', 3);

                req.onupgradeneeded = (ev) => {
                    const db = ev.target.result;
                    // mutations store
                    if (!db.objectStoreNames.contains('mutations'))
                        db.createObjectStore('mutations', { keyPath: 'chunkId' });
                    // playerState store (new in v2)
                    if (!db.objectStoreNames.contains('playerState'))
                        db.createObjectStore('playerState', { keyPath: 'id' });
                    // worldMeta store (new in v3)
                    if (!db.objectStoreNames.contains('worldMeta'))
                        db.createObjectStore('worldMeta', { keyPath: 'id' });
                };

                req.onsuccess = (ev) => {
                    this._db = ev.target.result;

                    // -- Load all mutations ------------------------------------
                    const loadMutations = () => new Promise((res) => {
                        try {
                            const tx    = this._db.transaction('mutations', 'readonly');
                            const store = tx.objectStore('mutations');
                            const getAll = store.getAll();
                            getAll.onsuccess = () => {
                                for (const rec of getAll.result || []) {
                                    const ch = this._mutations.getChunk(rec.chunkId);
                                    Object.assign(ch, rec.modifications || {});
                                }
                                try { this._applyMutationsToLoadedChunks(); } catch (_) {}
                                res();
                            };
                            getAll.onerror = () => res();
                        } catch (_) { res(); }
                    });

                    // -- Load player state -------------------------------------
                    const loadPlayerState = () => new Promise((res) => {
                        try {
                            const tx    = this._db.transaction('playerState', 'readonly');
                            const store = tx.objectStore('playerState');
                            const req2  = store.get('player');
                            req2.onsuccess = () => {
                                const ps = req2.result;
                                if (ps) {
                                    if (ps.inventory)   Object.assign(this._inventory, ps.inventory);
                                    if (typeof ps.health  === 'number') this._health  = ps.health;
                                    // shield not restored — always starts at 100
                                    if (typeof ps.lookYaw === 'number') this._lookYaw = ps.lookYaw;
                                    if (typeof ps.lookPitch === 'number') this._lookPitch = ps.lookPitch;
                                    // Stash position� applied in update() AFTER first chunks are built,
                                    // so the position restore doesn't invalidate the initial chunk set.
                                    // Skip if clearForJoin already ran: the IDB onsuccess is a macrotask
                                    // and can fire after clearForJoin cleared the restore, re-setting it
                                    // and overriding the host's spawn teleport on the next update tick.
                                    if (typeof ps.posX === 'number' && !this._blockPositionRestore) {
                                        this._pendingPositionRestore = { x: ps.posX, y: ps.posY, z: ps.posZ };
                                    }
                                    try { this._applyCameraRotation(); } catch (_) {}
                                    try {
                                        if (window.GameInterface) {
                                            window.GameInterface.updateResources(
                                                this._inventory.alloy, this._inventory.biomass,
                                                this._inventory.titanium, this._inventory.plasma,
                                                this._inventory.crystal
                                            );
                                        }
                                    } catch (_) {}
                                }
                                res();
                            };
                            req2.onerror = () => res();
                        } catch (_) { res(); }
                    });

                    // -- Load world metadata (seed + spawn) --------------------
                    const loadWorldMeta = () => new Promise((res) => {
                        try {
                            const tx    = this._db.transaction('worldMeta', 'readwrite');
                            const store = tx.objectStore('worldMeta');
                            const req2  = store.get('meta');
                            req2.onsuccess = () => {
                                const meta = req2.result;
                                if (meta && meta.noiseSeed !== undefined) {
                                    // Adopt the saved seed so the world is always the same
                                    if (meta.noiseSeed !== this.noiseSeed) {
                                        this.setSeed(meta.noiseSeed);
                                    }
                                    // Restore saved spawn point
                                    if (typeof meta.spawnCX === 'number') this._savedSpawnCX = meta.spawnCX;
                                    if (typeof meta.spawnCZ === 'number') this._savedSpawnCZ = meta.spawnCZ;
                                } else {
                                    // First time — write the current seed so future loads adopt it
                                    try {
                                        store.put({ id: 'meta', noiseSeed: this.noiseSeed, savedAt: Date.now(), version: 3 });
                                    } catch (_) {}
                                }
                                res();
                            };
                            req2.onerror = () => res();
                        } catch (_) { res(); }
                    });

                    Promise.all([loadWorldMeta(), loadMutations(), loadPlayerState()])
                        .catch(() => {})
                        .finally(() => resolve());
                };

                req.onerror = () => {
                    console.warn('[VoxelTreadmill] IndexedDB unavailable � running without persistence.');
                    resolve();
                };
            } catch (_) { resolve(); }
        });
    }

    // -- Auto-save player state ------------------------------------------------
    _startAutoSave() {
        if (this._autoSaveInterval) clearInterval(this._autoSaveInterval);
        this._autoSaveInterval = setInterval(() => this._savePlayerState(), 5000);
        // Also save on page unload / visibility change
        this._boundUnload = () => this._savePlayerState(true);
        window.addEventListener('pagehide',          this._boundUnload);
        window.addEventListener('visibilitychange',  () => { if (document.visibilityState === 'hidden') this._savePlayerState(true); });
    }

    _savePlayerState(sync = false) {
        if (!this._db) return;
        try {
            this.object.getPositionWorld(_wpBuf);
            const rec = {
                id:         'player',
                inventory:  Object.assign({}, this._inventory),
                health:     this._health,
                // shield not persisted — always reset to 100 by clearAllData() at run start
                lookYaw:    this._lookYaw,
                lookPitch:  this._lookPitch,
                posX:       -_wpBuf[0],
                posY:       -_wpBuf[1],
                posZ:       -_wpBuf[2],
                savedAt:    Date.now(),
            };
            const tx    = this._db.transaction('playerState', 'readwrite');
            const store = tx.objectStore('playerState');
            store.put(rec);
        } catch (e) {
            console.warn('[VoxelTreadmill] _savePlayerState error:', e);
        }
    }

    _queueChunkWrite(chunkId) {
        if (!this._mutations.hasChunk(chunkId)) return;
        this._dbWriteQueue.set(chunkId, Object.assign({}, this._mutations.peekChunk(chunkId)));
        if (this._dbFlushTimer) return;
        this._dbFlushTimer = setTimeout(() => this._flushDBWrites(), 120);
    }

    /** Force-flush all pending DB writes synchronously (e.g. on unload). */
    flushNow() { if (this._dbFlushTimer) { clearTimeout(this._dbFlushTimer); this._dbFlushTimer = null; } this._flushDBWrites(); }

    _flushDBWrites() {
        this._dbFlushTimer = null;
        if (!this._db || !this._dbWriteQueue.size) return;
        const tx    = this._db.transaction('mutations', 'readwrite');
        const store = tx.objectStore('mutations');
        for (const [chunkId, mods] of this._dbWriteQueue.entries()) {
            try { Object.keys(mods).length === 0 ? store.delete(chunkId) : store.put({ chunkId, modifications: mods, ts: Date.now() }); } catch (_) { }
        }
        this._dbWriteQueue.clear();
    }

    // -- World reset -----------------------------------------------------------

    /**
     * Save the canonical spawn point and seed to worldMeta so every session
     * returns the player to the same location.
     */
    saveWorldSpawn(cx, cz) {
        this._savedSpawnCX = cx;
        this._savedSpawnCZ = cz;
        if (!this._db) return;
        try {
            const tx = this._db.transaction('worldMeta', 'readwrite');
            tx.objectStore('worldMeta').put({ id: 'meta', noiseSeed: this.noiseSeed, spawnCX: cx, spawnCZ: cz, savedAt: Date.now(), version: 3 });
        } catch (_) {}
    }

    /**
     * Reset player vitals and inventory for a new run without touching world mutations.
     * Call on death-restart so the world persists but the player starts fresh.
     */
    resetPlayerStats() {
        this._inventory   = { alloy: 0, biomass: 0, titanium: 0, plasma: 0, crystal: 0, shieldCells: 0, medkits: 0 };
        this._health      = 100;
        this._shield      = 100;
        this._isDead      = false;
        this._velY        = 0;
        this._velX        = 0;
        this._velZ        = 0;
        this._pendingPositionRestore = null;
        try { if (window.GameInterface) window.GameInterface.updateResources(0, 0, 0, 0, 0); } catch (_) {}
        try { if (window.GameInterface) window.GameInterface.updateStats(this._health, this._shield); } catch (_) {}
    }

    /**
     * Full in-memory and DB clear for joiners. Wipes mutations/playerState but
     * PRESERVES worldMeta so the joiner's own solo world survives after the session.
     */
    async clearForJoin() {
        // Block any late-firing loadPlayerState IDB callback from re-setting _pendingPositionRestore
        // after we clear it — the IDB onsuccess is a macrotask and can fire after our null-clear.
        this._blockPositionRestore   = true;
        if (this._dbFlushTimer) { clearTimeout(this._dbFlushTimer); this._dbFlushTimer = null; this._dbWriteQueue.clear(); }
        this._mutations.clear();
        this._dataCache = new ChunkDataCache(512);
        for (const [, chunk] of this._chunks.entries()) {
            if (chunk.mesh) chunk.mesh.destroy();
            chunk.root.destroy();
        }
        this._chunks.clear();
        if (this._db) {
            try {
                const tx = this._db.transaction(['mutations', 'playerState'], 'readwrite');
                tx.objectStore('mutations').clear();
                tx.objectStore('playerState').clear();
                await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
            } catch (e) { console.warn('[VoxelTreadmill] clearForJoin DB error:', e); }
        }
        this._inventory   = { alloy: 0, biomass: 0, titanium: 0, plasma: 0, crystal: 0, shieldCells: 0, medkits: 0 };
        this._health      = 100;
        this._shield      = 100;
        this._isDead      = false;
        this._lookYaw     = 0;
        this._lookPitch   = 0;
        this._playerCX    = null;
        this._playerCZ    = null;
        this._pendingPositionRestore = null;
        // Keep _savedSpawnCX/CZ intact so solo resume still works after disconnecting
        try { if (window.GameInterface) window.GameInterface.updateResources(0, 0, 0, 0, 0); } catch (_) {}
        try { if (window.GameInterface) window.GameInterface.updateStats(this._health, this._shield); } catch (_) {}
    }

    /**
     * Wipe only the IndexedDB persistence (mutations + player state + world meta)
     * without touching in-memory chunks or teleporting the player.
     * Use at multiplayer session start so the next run begins clean while the
     * current in-game state remains intact.
     */
    async clearPersistedData() {
        if (this._dbFlushTimer) { clearTimeout(this._dbFlushTimer); this._dbFlushTimer = null; this._dbWriteQueue.clear(); }
        if (this._db) {
            try {
                const stores = ['mutations', 'playerState', 'worldMeta'];
                const tx = this._db.transaction(stores, 'readwrite');
                for (const name of stores) tx.objectStore(name).clear();
                await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
            } catch (e) { console.warn('[VoxelTreadmill] clearPersistedData DB error:', e); }
        }
    }

    async clearAllData() {
        if (this._dbFlushTimer) { clearTimeout(this._dbFlushTimer); this._dbFlushTimer = null; this._dbWriteQueue.clear(); }
        this._mutations.clear();
        this._dataCache = new ChunkDataCache(512);
        for (const [, chunk] of this._chunks.entries()) {
            if (chunk.mesh) chunk.mesh.destroy();
            chunk.root.destroy();
        }
        this._chunks.clear();
        if (this._db) {
            try {
                const stores = ['mutations', 'playerState', 'worldMeta'];
                const tx = this._db.transaction(stores, 'readwrite');
                for (const name of stores) tx.objectStore(name).clear();
                await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
            } catch (e) { console.error('[VoxelTreadmill] clearAllData DB error:', e); }
        }
        // Reset runtime player state
        this._inventory    = { alloy: 0, biomass: 0, titanium: 0, plasma: 0, crystal: 0, shieldCells: 0, medkits: 0 };
        this._health       = 100;
        this._shield       = 100;
        this._isDead       = false;
        this._lookYaw      = 0;
        this._lookPitch    = 0;
        this._playerCX     = null;
        this._playerCZ     = null;
        this._pendingPositionRestore = null;
        this._savedSpawnCX = null;
        this._savedSpawnCZ = null;
        this._pendingPositionRestore = null;
        try { if (window.GameInterface) window.GameInterface.updateResources(0, 0, 0, 0, 0); } catch (_) {}
        this.object.getPositionWorld(_wpBuf);
        this._updateWorld(Math.floor(-_wpBuf[0] / this.chunkSize), Math.floor(-_wpBuf[2] / this.chunkSize));
    }

    /** Update the world seed and re-initialize the sampler. 
     * Should be followed by a clearAllData() to refresh the world. */
    setSeed(seed) {
        this.noiseSeed = seed;
        this._hNoise = new SimplexNoise2D(this.noiseSeed);
        this._mNoise = new SimplexNoise2D(this.noiseSeed ^ 0xF00BA4);
        this._moistNoise = new SimplexNoise2D(this.noiseSeed ^ 0xA1B2C3);
        this._sampler = new WorldSampler(this._hNoise, this._mNoise, this._moistNoise, this.terrainHeight, this.terrainScale);
        this._dataCache = new ChunkDataCache(512);
    }
}